import { useAuth } from '@clerk/clerk-react'
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import {
  Flag,
  Loader2,
  RotateCw,
  X,
} from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { ChatInput, type ChatInputHandle } from '@/components/ChatInput'
import { VoiceMicButton, type VoiceHintKind } from '@/components/voice/VoiceMicButton'
import type { StoppedClip } from '@/components/voice/useMediaRecorder'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { authFetch, AuthFetchError } from '@/lib/api'
import { computeAutoStageCandidate } from '@/lib/auto-stage'
import { stashPendingVoiceClip } from '@/lib/pending-voice'
import { createDraftThread, threadKeys } from '@/lib/threads'
import { parseCustomerInput, parseReportPrefix, parseVehicleInput } from '@/lib/parse'
import {
  buildSuggestions,
  useStagedAttachment,
  type AttachmentSuggestion,
} from '@/lib/staged-attachment'
import type { EricPhotoContext } from '@/lib/eric-stream'

import { AttachmentChip } from './AttachmentChip'
import {
  AttachmentSuggestions,
  type AttachmentSuggestionHandlers,
} from './AttachmentSuggestions'
import {
  consumeContextId,
  isSlotPickerConsumed,
  selectSlotTapRoute,
} from './reply-card-booking'
import { SearchResults } from './SearchResults'
import type {
  CascadeCarryover,
  CascadeConflict,
  DispatchResponse,
  EricFocus,
  SearchResponse,
  SearchResult,
  Verb,
} from './types'

// ─────────────────────────────────────────────────────────────────────
// Verb config — what each verb is for, what kinds of entities its
// scoped search returns, and (where applicable) which field chips
// appear once a target is picked.
//
// Per memory [[feedback_agentic_ux_guides]]: agentic surfaces guide
// users step-by-step. Each verb is a cascade, not a freeform prompt.

const VERBS: Verb[] = [
  'book',
  'reschedule',
  'add_customer',
  'add_vehicle',
  'update',
]

const VERB_LABEL_KEY: Record<Verb, string> = {
  book: 'agentic.verb.book',
  reschedule: 'agentic.verb.reschedule',
  add_customer: 'agentic.verb.add_customer',
  add_vehicle: 'agentic.verb.add_vehicle',
  update: 'agentic.verb.update',
}

const VERB_SLASH: Record<Verb, string> = {
  book: '/b',
  reschedule: '/m',
  add_customer: '/c',
  add_vehicle: '/v',
  update: '/u',
}

const SLASH_TO_VERB = Object.fromEntries(
  Object.entries(VERB_SLASH).map(([k, v]) => [v, k as Verb]),
)

const VERB_SEARCH_KINDS: Record<Verb, string | null> = {
  book: 'customer',
  reschedule: 'appointment',
  update: 'customer,vehicle',
  // Add-* verbs don't need to pick an existing entity. The composer
  // skips the search step and goes straight to the freeform compose
  // mode with the scaffold pre-filled.
  add_customer: null,
  add_vehicle: null,
}

// ─────────────────────────────────────────────────────────────────────
// /u Update — the field set per entity kind. The chips below the
// target pill render from this. Picking a chip moves the composer to
// the `update_field` state, where the input becomes a value editor.

type UpdateField = {
  key: 'phone' | 'email' | 'address' | 'notes' | 'mileage' | 'color' | 'plate'
  labelKey: string
  placeholderKey: string
  inputMode?: 'numeric'
}

const CUSTOMER_FIELDS: UpdateField[] = [
  { key: 'phone', labelKey: 'agentic.update.field.phone', placeholderKey: 'agentic.update.placeholder.phone' },
  { key: 'email', labelKey: 'agentic.update.field.email', placeholderKey: 'agentic.update.placeholder.email' },
  { key: 'address', labelKey: 'agentic.update.field.address', placeholderKey: 'agentic.update.placeholder.address' },
  { key: 'notes', labelKey: 'agentic.update.field.notes', placeholderKey: 'agentic.update.placeholder.notes' },
]

const VEHICLE_FIELDS: UpdateField[] = [
  { key: 'mileage', labelKey: 'agentic.update.field.mileage', placeholderKey: 'agentic.update.placeholder.mileage', inputMode: 'numeric' },
  { key: 'notes', labelKey: 'agentic.update.field.notes', placeholderKey: 'agentic.update.placeholder.notes' },
  { key: 'color', labelKey: 'agentic.update.field.color', placeholderKey: 'agentic.update.placeholder.color' },
  { key: 'plate', labelKey: 'agentic.update.field.plate', placeholderKey: 'agentic.update.placeholder.plate' },
]

// ─────────────────────────────────────────────────────────────────────
// Composer state machine.
//
//   idle        → empty input, 5 verb chips visible
//   freeform    → user typed without picking a verb (search panel below)
//   verb        → verb picked, looking for target (scoped search panel)
//   target      → target picked; if /u, field chips appear; otherwise
//                 input becomes freeform-with-context (e.g. "Book Sarah Chen ...")
//   field       → /u + target + field picked; input is the value editor
//   preview     → low-confidence dispatch result
//   executed    → committed action (consumed by the host as a receipt)
//   reply       → free-form Eric ack (intent='other')

type ComposerState =
  | { mode: 'idle' }
  | { mode: 'freeform' }
  | { mode: 'verb'; verb: Verb }
  | { mode: 'target'; verb: Verb; target: SearchResult }
  | {
      // /b + customer or /m + appointment with a time slot staged but
      // not yet executed. Lets the user review the job description /
      // appointment context before sending. Slot can be peeled off
      // (× on the pill) or swapped (tap a different slot chip).
      mode: 'target_with_slot'
      verb: 'book' | 'reschedule'
      target: SearchResult
      slot: { starts_at_iso: string; label: string }
    }
  | {
      mode: 'field'
      verb: 'update'
      target: SearchResult
      field: UpdateField
    }

export function TaskEricInput({
  hideHero = false,
  micActive = true,
  onExecuted,
}: {
  hideHero?: boolean
  /**
   * The dock hides this composer with CSS (`display:none`), which unmounts
   * nothing — a locked (finger-lifted) recording would keep the mic hot
   * with zero visible UI and auto-send ambient audio at the 25s cap. The
   * host passes its visibility here; a false flips the mic's `disabled`,
   * whose existing effect cancels any live recorder (#231 lifecycle).
   */
  micActive?: boolean
  /**
   * Dock seam (PR-4, hardened in PR-14): every committed action
   * (dispatch `mode:'executed'` AND the fast-lane inline commits, which
   * synthesize the same shape) is reported to the host, which renders it
   * as a §4.1 receipt (ReceiptsHost) — the composer no longer renders
   * any executed surface of its own. REQUIRED so a future mount can't
   * silently swallow committed work: the legacy inline success banner
   * this replaced was deleted in PR-14.
   */
  onExecuted: (res: DispatchResponse) => void
}) {
  const { t } = useTranslation('dashboard')
  const { t: tVoice } = useTranslation('voice_messages')
  const { getToken, isLoaded } = useAuth()
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  // Dock voice — record here, converse THERE. A finished clip opens (or
  // reuses) the caller's "New chat" draft thread and hands the recording to
  // the thread workspace via the pending-voice slot; ThreadChat's own
  // submitVoice runs it through the normal transcribe → dispatch → bubble
  // pipeline. This surface never transcribes or dispatches voice itself —
  // one voice pipeline, one owner (Mira's 2026-07-23 thread-first call).
  // State, not a ref: the mic DISABLES during the handoff window (a second
  // recording started mid-handoff would only be dropped on release — don't
  // let it start), and disabling needs a render.
  const [handingOff, setHandingOff] = useState(false)
  const [voiceHint, setVoiceHint] = useState<VoiceHintKind | null>(null)
  const voiceHintTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => () => {
    if (voiceHintTimer.current) clearTimeout(voiceHintTimer.current)
  }, [])
  const showVoiceHint = (kind: VoiceHintKind) => {
    setVoiceHint(kind)
    if (voiceHintTimer.current) clearTimeout(voiceHintTimer.current)
    voiceHintTimer.current = setTimeout(() => setVoiceHint(null), 3000)
  }
  const onVoiceClip = async (clip: StoppedClip) => {
    if (handingOff) return
    setHandingOff(true)
    try {
      const { thread_id } = await createDraftThread(getToken)
      stashPendingVoiceClip(thread_id, clip)
      // The draft exists but the rail cache may not know for staleTime —
      // invalidate so the workspace header shows the row (label + Clear)
      // immediately instead of after the first turn's invalidate.
      queryClient.invalidateQueries({ queryKey: threadKeys.rail() })
      // Home's ?thread= seam selects the thread and cleans the param — the
      // archive deep-link path for cross-route arrivals, plus Today's
      // prev-snapshot select for same-route ones (dock on Today itself).
      navigate(`/?thread=${encodeURIComponent(thread_id)}`)
    } catch {
      showVoiceHint('failed')
    } finally {
      setHandingOff(false)
    }
  }

  const [composer, setComposer] = useState<ComposerState>({ mode: 'idle' })
  const [value, setValue] = useState('')
  // Per-conversation memory key for this Home "new chat". Minted once per mount
  // (a fresh dashboard visit = a fresh conversation) and sent on every turn so
  // Eric's Mastra memory is scoped to THIS chat — not the operator's clerkUserId,
  // which previously collapsed every dashboard chat into one memory thread and
  // bled the prior customer into the next. The server falls back to clerkUserId
  // only when this is absent (un-upgraded client). See worker buildMemoryKey.
  const [conversationId] = useState(() => crypto.randomUUID())
  // Live "report mode": the message starts with `report:`, so this turn logs a
  // problem instead of going to Eric. The server backstop in /eric/dispatch/stream
  // does the actual divert + save; this drives the calm pre-send affordance + the
  // onSubmit short-circuit (so no cascade branch can intercept a report).
  const reportMode = parseReportPrefix(value).isReport
  const [debouncedQ, setDebouncedQ] = useState('')
  const [dispatchResult, setDispatchResult] = useState<DispatchResponse | null>(null)
  // Blank-card safety net (Fix 4). A turn can end with no usable reply — a
  // transport fault, a stream that closed before any text, or an empty finalize
  // the server fallback couldn't recover. Instead of a silent blank card we
  // surface a retry affordance (the amber card below) that re-runs the same turn.
  const [dispatchFailed, setDispatchFailed] = useState(false)
  // Part C: the server's honest "didn't finish" reply for an incomplete turn —
  // shown in the retry card body so the mutating-variant warning ("part of that
  // may have gone through - check the record before retrying") reaches the user
  // live, not only via the persisted thread on reload. Null = generic copy.
  const [incompleteReply, setIncompleteReply] = useState<string | null>(null)
  const [lastDispatch, setLastDispatch] = useState<{
    prompt: string
    photo?: EricPhotoContext
  } | null>(null)
  // Settle a fresh dispatch result and clear any prior failure state in one shot.
  // Every place that lands a NON-failure result (the stream success path AND the
  // inline cascade/update mutations) routes through this so a stale `dispatchFailed`
  // can't leave the amber retry card stacked behind the green result card, or let
  // Retry re-run an unrelated earlier turn.
  const settleDispatchResult = (result: DispatchResponse) => {
    setDispatchResult(result)
    setDispatchFailed(false)
    setIncompleteReply(null)
    setLastDispatch(null)
  }
  // Sticky context across a dispatch — survives composer reset so the
  // reply card knows the last verb/target/vehicle was a book or
  // reschedule and can render an inline slot strip when the brain
  // asks for a time. Reset whenever a fresh idle/freeform submit fires.
  // `id` is a monotonic generation counter (see startDispatchContext);
  // `note` carries the user's typed text so a fast-lane slot tap can
  // file it as the appointment notes, matching the composer cascade.
  const [lastDispatchContext, setLastDispatchContext] = useState<{
    id: number
    verb: Verb | null
    customer: SearchResult | null
    vehicle: SearchResult | null
    appointment: SearchResult | null
    note: string | null
  }>({
    id: 0,
    verb: null,
    customer: null,
    vehicle: null,
    appointment: null,
    note: null,
  })
  // Mirrors the live context generation for async settle paths (the stream
  // closure captures pre-submit state, so it reads this ref instead). Fresh
  // submits bump it via startDispatchContext; the reply-card slot-tap /
  // customer-pick re-dispatches deliberately reuse the current generation.
  const dispatchContextIdRef = useRef(0)
  const startDispatchContext = (ctx: {
    verb: Verb | null
    customer: SearchResult | null
    vehicle: SearchResult | null
    appointment: SearchResult | null
    note: string | null
  }) => {
    dispatchContextIdRef.current += 1
    setLastDispatchContext({ id: dispatchContextIdRef.current, ...ctx })
  }
  // The context generation whose booking settled successfully. The slot
  // picker rendered for that generation is CONSUMED — inert + visually
  // disabled — so a stale "Tap a slot to book" strip can't book a second
  // appointment. Set on BOTH paths: the fast-lane book/reschedule mutations
  // (per-call onSuccess, capturing the id at call time) and the brain
  // dispatch settle (mode='executed'). See reply-card-booking.ts.
  const [consumedContextId, setConsumedContextId] = useState<number | null>(
    null,
  )

  // Tracks whether the most recent dispatch was triggered by tapping a
  // slot chip on the brain reply card. Lets the card stay mounted
  // through the 3-6s brain round-trip (no flash-back-to-empty) while
  // showing an inline "Booking…" pill in place of the slot strip.
  // Cleared the moment the next dispatch settles.
  // Doubles as the pending flag for the reply-card CUSTOMER picker (below) —
  // both inline reply-card picks keep the card mounted + show a pill through
  // the brain round-trip; the reset sites clear it for both.
  const [slotTapPending, setSlotTapPending] = useState(false)

  // Reply-card customer picker (the missing-customer analog of the slot strip):
  // when a freeform `book` comes back as a reply because the customer is
  // ambiguous/unresolved, surface an inline customer combobox so the cascade
  // isn't abandoned to /customers. Its own debounced search text, kept separate
  // from the composer's `value`.
  const [replyCustomerQ, setReplyCustomerQ] = useState('')
  const [replyCustomerDebounced, setReplyCustomerDebounced] = useState('')

  // Staged-attachment flow (replaces the scattered attach* / attachSuggestion /
  // vinNoFocusOffer state). A photo attached via the paperclip (ChatInput's
  // attachment slot) stages in the composer as a thumbnail and classifies in
  // the background; the result drives content-aware action chips. Nothing
  // auto-acts — the user taps a chip or hits Send. See src/lib/staged-attachment.ts.
  // The hook flips `staged.status` to 'error' on a classify/consume failure;
  // <AttachmentChip> renders that as an inline "Couldn't read that photo." line
  // and the user can ✕ + re-attach. No separate error state to surface.
  const stagedAttachment = useStagedAttachment({ getToken })
  const staged = stagedAttachment.staged
  // The shop's most-recently-active customer, fetched lazily when a photo
  // stages with no conversational focus. Backstops the deterministic
  // create-chips so a no-focus VIN/receipt can still lead with a real owner.
  const [recentCustomer, setRecentCustomer] = useState<{
    id: string
    name: string
  } | null>(null)
  // The customer/vehicle Eric resolved on the most recent dispatch turn,
  // carried back on the stream's `done` envelope. Sticky on purpose — only a
  // NEW non-null focus overwrites it, so a follow-up "go ahead, sending the
  // VIN" turn (which resolves nothing itself) keeps the customer from the turn
  // that did. Feeds the suggestion builder so a staged VIN photo pre-fills the
  // owner the conversation just established. Server-side backstop for reloads:
  // /api/eric/dashboard-focus (read by VehicleFromPhoto).
  const [conversationFocus, setConversationFocus] = useState<EricFocus | null>(
    null,
  )

  // Inline error chip for cascade-route 403s (Eric permission denied for
  // the active role). Cleared when the user dismisses or starts a new
  // submission.
  const [cascadeError, setCascadeError] = useState<
    { kind: 'forbidden'; capability: string } | { kind: 'failed' } | null
  >(null)
  // Haiku-cleaned version of the booking note offered as an inline
  // diff INSIDE the input card (not as a separate tile). User picks
  // explicitly — Use Eric's / Modify / Keep mine. Clears whenever the
  // composer state changes (new target picked, sent, peeled back).
  const [haikuSuggestion, setHaikuSuggestion] = useState<string | null>(null)
  // Two-step Modify: first click opens the "edit which?" picker
  // (Edit mine | Edit Eric's), avoiding a popover dependency.
  const [modifyChoosing, setModifyChoosing] = useState(false)
  // Imperative handle into the shared ChatInput (focus on cascade
  // transitions + after the haiku-diff buttons swap the value back in).
  const chatInputRef = useRef<ChatInputHandle | null>(null)
  // Aborts the in-flight dispatch stream — on unmount (so a navigated-away
  // stream stops reading + setting state) and when a newer dispatch supersedes
  // it. See runDispatchStream.
  const dispatchAbortRef = useRef<AbortController | null>(null)
  useEffect(() => () => dispatchAbortRef.current?.abort(), [])

  // Debounce input → search query.
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedQ(value.trim()), 150)
    return () => clearTimeout(handle)
  }, [value])

  // Debounce the reply-card customer picker's own search box.
  useEffect(() => {
    const handle = setTimeout(() => setReplyCustomerDebounced(replyCustomerQ.trim()), 150)
    return () => clearTimeout(handle)
  }, [replyCustomerQ])

  // Slash shortcut detection runs in the composer's onChange handler
  // (see `onComposerChange` below) — typed `/u ` in idle becomes the
  // Update verb. Lives in the event handler rather than a useEffect so
  // the slash trigger doesn't bounce through a render cycle (and so it
  // stays clear of the react-hooks/set-state-in-effect lint rule).

  // Auto-focus the textarea whenever the composer transitions to a
  // state where the user is expected to type next.
  useEffect(() => {
    if (
      composer.mode === 'verb' ||
      composer.mode === 'target' ||
      composer.mode === 'target_with_slot' ||
      composer.mode === 'field'
    ) {
      requestAnimationFrame(() => chatInputRef.current?.focus())
    }
  }, [composer.mode])

  // Clear the Haiku suggestion any time the composer leaves the
  // `target` state — peeling back, sending, picking a different
  // suggestion all reset the suggestion so it never lingers out of
  // context. Pattern A: in-render setState gated by a prev-mode
  // snapshot, per the React "You Might Not Need an Effect" idiom — the
  // current paint is discarded and the next render reflects the reset
  // value, no flicker.
  const [prevComposerModeForHaiku, setPrevComposerModeForHaiku] =
    useState(composer.mode)
  if (composer.mode !== prevComposerModeForHaiku) {
    setPrevComposerModeForHaiku(composer.mode)
    if (
      composer.mode !== 'target' &&
      composer.mode !== 'target_with_slot'
    ) {
      setHaikuSuggestion(null)
      setModifyChoosing(false)
    }
  }

  // ─── Builder transitions ────────────────────────────────────────────

  const startVerb = (verb: Verb) => {
    setDispatchResult(null)
    setValue('')
    setDebouncedQ('')
    // Tapping a verb chip moves the composer on — drop any staged photo from a
    // prior upload so it can't stack above the new breadcrumb.
    stagedAttachment.clear()
    if (VERB_SEARCH_KINDS[verb] === null) {
      // Add-* verbs: skip the search step. Drop the user into a
      // freeform compose where they fill in details below the pill.
      setComposer({ mode: 'verb', verb })
    } else {
      setComposer({ mode: 'verb', verb })
    }
  }

  const onTargetPicked = (target: SearchResult) => {
    if (composer.mode === 'verb') {
      setValue('')
      setDebouncedQ('')
      setComposer({ mode: 'target', verb: composer.verb, target })
      return
    }
    // Idle / freeform with an actionable verb in the prompt → stage
    // the cascade with the picked record instead of letting the
    // search-result tile navigate to the record page. Lets the user
    // type "book oliv" + click the Olivia Martin tile and land on
    // Book → Olivia Martin ready for a slot tap, same as the
    // auto-stage useEffect would have done if the search had
    // surfaced exactly one match.
    if (composer.mode === 'idle' || composer.mode === 'freeform') {
      const verb = parseFreeformIntent(value)
      if (verb === 'book' && target.kind === 'customer') {
        setValue('')
        setDebouncedQ('')
        setComposer({ mode: 'target', verb, target })
        return
      }
      if (verb === 'reschedule' && target.kind === 'appointment') {
        setValue('')
        setDebouncedQ('')
        setComposer({ mode: 'target', verb, target })
        return
      }
    }
    // No verb context — fall through to default navigate (SearchResults
    // tile uses `navigate(r.url)` when onPick is undefined).
  }

  const onFieldPicked = (field: UpdateField) => {
    if (composer.mode !== 'target' || composer.verb !== 'update') return
    setValue('')
    setComposer({
      mode: 'field',
      verb: 'update',
      target: composer.target,
      field,
    })
  }

  const peelBack = () => {
    if (composer.mode === 'field') {
      setComposer({
        mode: 'target',
        verb: 'update',
        target: composer.target,
      })
      setValue('')
      return
    }
    if (composer.mode === 'target_with_slot') {
      // Peel off the staged slot only — keep the customer / appointment.
      setComposer({
        mode: 'target',
        verb: composer.verb,
        target: composer.target,
      })
      return
    }
    if (composer.mode === 'target') {
      setComposer({ mode: 'verb', verb: composer.verb })
      setValue('')
      return
    }
    if (composer.mode === 'verb') {
      setComposer({ mode: 'idle' })
      setValue('')
      return
    }
    setComposer({ mode: 'idle' })
    setValue('')
  }

  // ─── Search (scoped by composer state) ──────────────────────────────

  const searchKinds =
    composer.mode === 'verb'
      ? VERB_SEARCH_KINDS[composer.verb]
      : composer.mode === 'idle' || composer.mode === 'freeform'
        ? 'customer,vehicle,appointment'
        : null

  const searchEnabled =
    isLoaded &&
    debouncedQ.length >= 2 &&
    searchKinds !== null &&
    dispatchResult === null

  const searchQuery = useQuery({
    queryKey: ['agentic-search', searchKinds, debouncedQ],
    enabled: searchEnabled,
    queryFn: () =>
      authFetch<SearchResponse>(
        `/api/search?q=${encodeURIComponent(debouncedQ)}&kinds=${searchKinds}&limit=8`,
        getToken,
      ),
    // keepPreviousData stops the result list from flickering data -> [] -> data
    // every keystroke (each `debouncedQ` is a new cache key). Without it the
    // rows shift/empty under the user's finger mid-fetch, so a tap can stage a
    // different/absent customer than the one shown. While the new query is in
    // flight `isPlaceholderData` is true and we make the tiles non-tappable
    // (see `stale` on <SearchResults>), so a pick always matches what's shown.
    placeholderData: keepPreviousData,
    staleTime: 10_000,
  })

  // Name-only fallback search for the auto-stage flow. `/api/search`
  // does a single ILIKE on the full query string (worker/routes/
  // dashboard.ts:476-484), so `book olivia for wednesday` never matches
  // "Olivia Martin" — 0 results, auto-stage useEffect stays idle. When
  // we detect a book/reschedule intent AND the full-phrase search has
  // settled with 0 results, we extract just the name portion from the
  // freeform value and fire a narrower second search. Used purely as a
  // fallback signal for the auto-stage gate below — it doesn't drive
  // the visible search dropdown.
  const fallbackVerb =
    composer.mode === 'idle' || composer.mode === 'freeform'
      ? parseFreeformIntent(debouncedQ)
      : null
  const fallbackName =
    fallbackVerb === 'book' || fallbackVerb === 'reschedule'
      ? extractNameQuery(debouncedQ, fallbackVerb)
      : null
  const fallbackKinds =
    fallbackVerb === 'reschedule' ? 'appointment' : 'customer'
  const nameOnlySearchEnabled =
    isLoaded &&
    fallbackName !== null &&
    fallbackName.length >= 3 &&
    fallbackName !== debouncedQ &&
    searchEnabled &&
    !searchQuery.isLoading &&
    (searchQuery.data?.results.length ?? 0) === 0
  const nameOnlySearchQuery = useQuery({
    queryKey: ['agentic-search-name-only', fallbackKinds, fallbackName],
    enabled: nameOnlySearchEnabled,
    queryFn: () =>
      authFetch<SearchResponse>(
        `/api/search?q=${encodeURIComponent(fallbackName ?? '')}&kinds=${fallbackKinds}&limit=2`,
        getToken,
      ),
    staleTime: 10_000,
  })

  // Default-state list for /m Reschedule: show upcoming appointments
  // before the user types anything. The eventual story is that this
  // is also where Eric surfaces context gathered elsewhere — the
  // appointment Emil's currently on the phone about will float to the
  // top once we wire Twilio audio context.
  const upcomingEnabled =
    isLoaded &&
    composer.mode === 'verb' &&
    composer.verb === 'reschedule' &&
    debouncedQ.length === 0 &&
    dispatchResult === null
  const upcomingQuery = useQuery({
    queryKey: ['agentic-upcoming-appointments'],
    enabled: upcomingEnabled,
    queryFn: () =>
      authFetch<{ results: SearchResult[] }>(
        '/api/dashboard/upcoming-appointments?limit=10',
        getToken,
      ),
    staleTime: 30_000,
  })

  // Default-state list for /b Book: ranked suggestions from active
  // carryover notes + urgency-keyword scan on customer/vehicle notes.
  // When the Twilio audio context lands the same endpoint reranks so
  // the customer currently on the phone floats to the top.
  const bookingSuggestEnabled =
    isLoaded &&
    composer.mode === 'verb' &&
    composer.verb === 'book' &&
    debouncedQ.length === 0 &&
    dispatchResult === null
  const bookingSuggestQuery = useQuery({
    queryKey: ['agentic-booking-suggestions'],
    enabled: bookingSuggestEnabled,
    queryFn: () =>
      authFetch<{
        results: Array<
          SearchResult & {
            reason: string
            urgency: 'high' | 'medium' | 'low'
            note_context: string | null
          }
        >
      }>('/api/dashboard/booking-suggestions?limit=10', getToken),
    staleTime: 30_000,
  })

  // Available time slots — used by BOTH /m Reschedule (after picking
  // an appointment to move) and /b Book (after picking a customer).
  // Once Twilio audio context lands the same endpoint will accept a
  // preference window from "what we overheard" — frontend contract
  // unchanged.
  const availableSlotsEnabled =
    isLoaded &&
    (composer.mode === 'target' || composer.mode === 'target_with_slot') &&
    (composer.verb === 'reschedule' || composer.verb === 'book') &&
    dispatchResult === null
  // Pull the target+verb out of the composer state for the query key
  // + URL so the query stays stable across `target` ↔ `target_with_slot`
  // transitions (we don't want it to refetch when the user just stages
  // a slot — the underlying open-slot list hasn't changed).
  const slotsContext =
    composer.mode === 'target' || composer.mode === 'target_with_slot'
      ? { verb: composer.verb, targetId: composer.target.id }
      : null
  const availableSlotsQuery = useQuery({
    queryKey: [
      'agentic-available-slots',
      slotsContext?.verb ?? null,
      slotsContext?.targetId ?? null,
    ],
    enabled: availableSlotsEnabled,
    queryFn: () => {
      // Exclude only applies to /m (so the appt being moved doesn't
      // block its own slot). /b doesn't need an exclude.
      const exclude =
        slotsContext?.verb === 'reschedule'
          ? `&exclude=${slotsContext.targetId}`
          : ''
      return authFetch<{
        slots: Array<{ starts_at_iso: string; label: string }>
      }>(
        `/api/dashboard/available-slots?limit=6&duration_min=60${exclude}`,
        getToken,
      )
    },
    staleTime: 30_000,
  })

  // Reply-card slot strip: after a book/reschedule dispatch where the
  // brain came back with mode='reply' (asking for time), surface a
  // tap-to-pick strip so the user can complete the booking in one
  // click instead of typing the time and re-sending. Composer has
  // already been reset to idle by then, so we key off
  // lastDispatchContext, which survives across the dispatch.
  const replyCardSlotsEnabled =
    isLoaded &&
    dispatchResult?.mode === 'reply' &&
    (lastDispatchContext.verb === 'book' ||
      lastDispatchContext.verb === 'reschedule')
  const replyCardSlotsQuery = useQuery({
    queryKey: [
      'agentic-reply-card-slots',
      lastDispatchContext.verb,
      lastDispatchContext.appointment?.id ?? null,
    ],
    enabled: replyCardSlotsEnabled,
    queryFn: () => {
      // Reschedule excludes the appointment being moved so it doesn't
      // block its own slot. Book has no exclude.
      const exclude =
        lastDispatchContext.verb === 'reschedule' &&
        lastDispatchContext.appointment
          ? `&exclude=${lastDispatchContext.appointment.id}`
          : ''
      return authFetch<{
        slots: Array<{ starts_at_iso: string; label: string }>
      }>(
        `/api/dashboard/available-slots?limit=6&duration_min=60${exclude}`,
        getToken,
      )
    },
    staleTime: 30_000,
  })

  // Reply-card customer picker: a freeform `book` that came back as a reply
  // BECAUSE the customer is ambiguous/unresolved (Eric asks "which customer?").
  // We gate on a disambiguation cue in the reply text — NOT just "book + no
  // staged customer" — so a "what time?" reply still gets the slot strip, never
  // this picker. Surfacing the combobox inline keeps the cascade alive instead
  // of dumping the user on /customers.
  const replyCustomerPickerEnabled =
    isLoaded &&
    dispatchResult?.mode === 'reply' &&
    lastDispatchContext.verb === 'book' &&
    !lastDispatchContext.customer &&
    replyAsksForCustomer(dispatchResult.reply ?? '')
  const replyCustomerSearchQuery = useQuery({
    queryKey: ['agentic-reply-card-customer', replyCustomerDebounced],
    enabled: replyCustomerPickerEnabled && replyCustomerDebounced.length >= 2,
    queryFn: () =>
      authFetch<SearchResponse>(
        `/api/search?q=${encodeURIComponent(replyCustomerDebounced)}&kinds=customer&limit=6`,
        getToken,
      ),
    staleTime: 10_000,
  })

  // Haiku-driven note summarizer — interim bandaid for booking-
  // suggestion prefill quality. The booking-suggestion click sets a
  // mechanical first-sentence prefill immediately, then fires this
  // mutation in the background. When the summary lands it surfaces
  // as a strike-diff suggestion tile (haikuSuggestion) — skipped if
  // it matches what's already in the textarea.
  const summarizeNote = useMutation({
    // target_id rides the mutation variables only — the API payload
    // stays {note, customer_name}.
    mutationFn: ({
      note,
      customer_name,
    }: {
      note: string
      customer_name?: string
      target_id: string
    }) =>
      authFetch<{ summary: string }>(
        '/api/dashboard/summarize-note-for-booking',
        getToken,
        { method: 'POST', body: JSON.stringify({ note, customer_name }) },
      ),
    onSuccess: (data, variables) => {
      // The summary can land after the user already sent or peeled the
      // cascade back — the mode-change reset has run by then, and a
      // stale diff tile must not hijack the idle composer.
      if (
        composer.mode !== 'target' &&
        composer.mode !== 'target_with_slot'
      )
        return
      // …or after the user re-picked a DIFFERENT target within the
      // summarizer's latency: a summary for A must not surface under B.
      if (composer.target.id !== variables.target_id) return
      const summary = data.summary?.trim() ?? ''
      // Only surface as a tile if Haiku produced something useful AND
      // it actually differs from what's already in the textarea.
      if (!summary) return
      if (summary === value.trim()) return
      setHaikuSuggestion(summary)
    },
  })

  // Translates a cascade mutation error into the inline 403 chip when
  // the server replied with the `forbidden` envelope. Anything else is
  // re-thrown so the existing error state on the mutation still flips.
  const handleCascadeError = (err: unknown) => {
    if (err instanceof AuthFetchError && err.status === 403) {
      const body = err.body as { required_permission?: unknown } | null
      const cap =
        body && typeof body.required_permission === 'string'
          ? body.required_permission
          : 'unknown'
      setCascadeError({ kind: 'forbidden', capability: cap })
      setDispatchResult(null)
      return
    }
    console.error(
      JSON.stringify({
        event: 'task_eric_input.cascade_failed',
        error: err instanceof Error ? err.message : String(err),
      }),
    )
    // Every cascade failure must be visible — a console-only error reads as
    // a silent no-op to the mechanic (the staged photo is already cleared).
    setCascadeError({ kind: 'failed' })
  }

  // /m Reschedule path — also exposes cancel. Same skip-the-classifier
  // pattern as the other inline mutations. The server returns the real
  // assistant_requests row id as `request_id`, which the banner uses to
  // POST undo within the 5-minute window.
  const cancelAppointment = useMutation({
    mutationFn: (payload: { appointment_id: string }) =>
      authFetch<{
        ok: boolean
        id: string
        url: string
        request_id: string | null
      }>('/api/dashboard/cancel-appointment', getToken, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['agentic-load'] })
      queryClient.invalidateQueries({ queryKey: ['agentic-needs-action'] })
      queryClient.invalidateQueries({
        queryKey: ['agentic-upcoming-appointments'],
      })
      settleDispatchResult({
        request_id: data.request_id ?? 'inline-cancel',
        mode: 'executed',
        intent: 'cancel_appointment',
        summary: 'Cancelled',
        why_low_confidence: null,
        confidence: 1,
        args: {},
        reply: 'Appointment cancelled.',
        result: { id: data.id, label: 'Appointment', url: data.url },
      })
      setComposer({ mode: 'idle' })
      setValue('')
    },
    onError: handleCascadeError,
  })

  // /b Book — one-tap booking with the prefilled notes + chosen slot.
  // Same skip-the-classifier pattern as reschedule + update-field.
  const book = useMutation({
    mutationFn: (payload: {
      customer_id: string
      starts_at_iso: string
      notes: string | null
    }) =>
      authFetch<{
        ok: boolean
        id: string
        customer: { id: string; name: string }
        starts_at_iso: string
        url: string
        request_id: string | null
        conflicts?: CascadeConflict[]
        carryover?: CascadeCarryover[]
      }>('/api/dashboard/book', getToken, {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['agentic-load'] })
      queryClient.invalidateQueries({ queryKey: ['agentic-needs-action'] })
      queryClient.invalidateQueries({
        queryKey: ['agentic-upcoming-appointments'],
      })
      queryClient.invalidateQueries({
        queryKey: ['agentic-booking-suggestions'],
      })
      settleDispatchResult({
        request_id: data.request_id ?? 'inline-book',
        mode: 'executed',
        intent: 'book_appointment',
        summary: `Booked ${data.customer.name}`,
        why_low_confidence: null,
        confidence: 1,
        args: {},
        reply: `Booked ${data.customer.name}.`,
        result: { id: data.id, label: data.customer.name, url: data.url },
        conflicts: data.conflicts,
        carryover: data.carryover,
      })
      setComposer({ mode: 'idle' })
      setValue('')
    },
    onError: handleCascadeError,
  })

  // One-tap reschedule mutation — skip the classifier; we already
  // know the exact appointment id + the exact new slot.
  const reschedule = useMutation({
    mutationFn: (payload: {
      appointment_id: string
      new_starts_at_iso: string
    }) =>
      authFetch<{
        ok: boolean
        id: string
        new_starts_at_iso: string
        url: string
        request_id: string | null
        conflicts?: CascadeConflict[]
        carryover?: CascadeCarryover[]
      }>('/api/dashboard/reschedule', getToken, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['agentic-load'] })
      queryClient.invalidateQueries({ queryKey: ['agentic-needs-action'] })
      queryClient.invalidateQueries({
        queryKey: ['agentic-upcoming-appointments'],
      })
      settleDispatchResult({
        request_id: data.request_id ?? 'inline-reschedule',
        mode: 'executed',
        intent: 'reschedule_appointment',
        summary: 'Rescheduled',
        why_low_confidence: null,
        confidence: 1,
        args: {},
        reply: 'Rescheduled.',
        result: { id: data.id, label: 'Appointment', url: data.url },
        conflicts: data.conflicts,
        carryover: data.carryover,
      })
      setComposer({ mode: 'idle' })
      setValue('')
    },
    onError: handleCascadeError,
  })

  // Default-state list for /v Add vehicle: customers with no vehicle
  // on file. Most common reason to add a vehicle is "I just took down
  // this customer's info, now I need their car" — these are exactly
  // those records. Tap → composer enters target state with the
  // customer in tow, then types year/make/model/VIN → atomic create.
  const customersWoVehiclesEnabled =
    isLoaded &&
    composer.mode === 'verb' &&
    composer.verb === 'add_vehicle' &&
    debouncedQ.length === 0 &&
    dispatchResult === null
  const customersWoVehiclesQuery = useQuery({
    queryKey: ['agentic-customers-without-vehicles'],
    enabled: customersWoVehiclesEnabled,
    queryFn: () =>
      authFetch<{
        results: Array<SearchResult & { reason: string }>
      }>('/api/dashboard/customers-without-vehicles?limit=10', getToken),
    staleTime: 30_000,
  })

  // Add-vehicle-for-customer mutation — atomic create + link. The
  // mutationFn handles the server's 409 vehicle_exists response inline
  // (the server returns the existing record) so the cascade can render
  // "X already has this vehicle — open it" instead of a generic error.
  const addVehicleForCustomer = useMutation({
    mutationFn: async (payload: {
      // null = orphan create (no owner) — the no-focus VIN "Save as orphan"
      // path. The endpoint stores the row with customer_id null and still
      // records an `add_vehicle` undo row, so the Undo pill reverses it too.
      customer_id: string | null
      year: number | null
      make: string | null
      model: string | null
      vin: string | null
    }): Promise<
      | {
          kind: 'created'
          vehicle: { id: string; label: string; url: string }
          customer: { id: string; name: string } | null
          request_id: string | null
        }
      | {
          kind: 'duplicate'
          existing: { id: string; label: string; url: string }
          customer: { id: string; name: string } | null
        }
    > => {
      const token = await getToken()
      if (!token) throw new Error('Missing Clerk token')
      const res = await fetch('/api/dashboard/add-vehicle-for-customer', {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      })
      if (res.status === 409) {
        const body = (await res.json()) as {
          existing: { id: string; label: string; url: string }
          customer: { id: string; name: string } | null
        }
        return { kind: 'duplicate', ...body }
      }
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        let parsed: unknown
        try {
          parsed = text ? JSON.parse(text) : null
        } catch {
          parsed = text || null
        }
        throw new AuthFetchError(
          res.status,
          parsed,
          text || `Request failed (${res.status})`,
        )
      }
      const body = (await res.json()) as {
        ok: boolean
        vehicle: { id: string; label: string; url: string }
        customer: { id: string; name: string } | null
        request_id: string | null
      }
      return {
        kind: 'created' as const,
        vehicle: body.vehicle,
        customer: body.customer,
        request_id: body.request_id,
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ['agentic-customers-without-vehicles'],
      })
      queryClient.invalidateQueries({ queryKey: ['agentic-update-suggestions'] })
      if (data.kind === 'duplicate') {
        // Dedup hit doesn't mutate, so there's no real request_id and
        // no undo to offer. The banner still renders Open + Dismiss.
        // customer is null on an orphan-path dedup (the matching VIN may be
        // owned by someone else or no one) — phrase it without an owner.
        const dupeSummary = data.customer
          ? `${data.customer.name} already has ${data.existing.label}`
          : `${data.existing.label} is already on file`
        const dupeReply = data.customer
          ? `${data.customer.name} already has ${data.existing.label} on file. Open it instead of adding a duplicate.`
          : `${data.existing.label} is already on file. Open it instead of adding a duplicate.`
        settleDispatchResult({
          request_id: 'inline-add-vehicle-dupe',
          mode: 'executed',
          intent: 'add_vehicle_for_customer',
          summary: dupeSummary,
          why_low_confidence: null,
          confidence: 1,
          args: {},
          reply: dupeReply,
          result: {
            id: data.existing.id,
            label: data.existing.label,
            url: data.existing.url,
          },
        })
      } else {
        // Orphan create (customer null) → no owner yet; phrase it so the
        // mechanic knows to assign one later (the /c Add-customer cascade
        // surfaces ownerless vehicles for exactly this).
        const okSummary = data.customer
          ? `Added ${data.vehicle.label} for ${data.customer.name}`
          : `Added ${data.vehicle.label} with no owner`
        const okReply = data.customer
          ? `Added ${data.vehicle.label} for ${data.customer.name}.`
          : `Added ${data.vehicle.label} with no owner yet — assign one from /vehicles when you know it.`
        settleDispatchResult({
          request_id: data.request_id ?? 'inline-add-vehicle',
          mode: 'executed',
          intent: 'add_vehicle_for_customer',
          summary: okSummary,
          why_low_confidence: null,
          confidence: 1,
          args: {},
          reply: okReply,
          result: {
            id: data.vehicle.id,
            label: data.vehicle.label,
            url: data.vehicle.url,
          },
        })
      }
      setComposer({ mode: 'idle' })
      setValue('')
    },
    onError: handleCascadeError,
  })

  // Log-mileage mutation (P2) — the odometer chip's fast lane. Appends a
  // `vehicle_mileage_history` row (source 'photo', photo_r2_key) and bumps
  // `vehicles.mileage`, with an undoable `log_mileage` audit row. Mirrors
  // addVehicleForCustomer: settles an executed banner + Undo on success.
  const logMileageForPhoto = useMutation({
    mutationFn: (payload: {
      vehicle_id: string
      mileage_km: number
      photo_r2_key: string | null
    }) =>
      authFetch<{
        ok: boolean
        mileage: {
          history_id: string
          vehicle_id: string
          mileage_km: number
          label: string
          url: string
        }
        request_id: string | null
      }>('/api/dashboard/log-mileage', getToken, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['agentic-update-suggestions'] })
      // Backend nests the entity under `mileage`; `label` is the pre-formatted
      // "{N} km on {vehicle}" string (see PATCH /dashboard/log-mileage).
      const summary = `Logged ${data.mileage.label}`
      settleDispatchResult({
        request_id: data.request_id ?? 'inline-log-mileage',
        mode: 'executed',
        intent: 'log_mileage',
        summary,
        why_low_confidence: null,
        confidence: 1,
        args: {},
        reply: `${summary}.`,
        result: {
          id: data.mileage.vehicle_id,
          label: data.mileage.label,
          url: data.mileage.url,
        },
      })
      setComposer({ mode: 'idle' })
      setValue('')
    },
    onError: handleCascadeError,
  })

  // Save-attachment mutation (P3) — the receipt chip's fast lane. Inserts a
  // `vehicle_attachments` row scoped to the customer, with an undoable
  // `add_attachment` audit row. Settles an executed banner + Undo on success.
  const saveAttachment = useMutation({
    mutationFn: (payload: {
      photo_r2_key: string
      customer_id: string | null
      total_cents: number | null
      currency: string | null
    }) =>
      authFetch<{
        ok: boolean
        attachment: { id: string; label: string; url: string }
        customer: { id: string; name: string } | null
        request_id: string | null
      }>('/api/dashboard/save-attachment', getToken, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      }),
    onSuccess: (data) => {
      const summary = data.customer
        ? `Saved receipt to ${data.customer.name}`
        : `Saved receipt`
      settleDispatchResult({
        request_id: data.request_id ?? 'inline-save-attachment',
        mode: 'executed',
        intent: 'add_attachment',
        summary,
        why_low_confidence: null,
        confidence: 1,
        args: {},
        reply: `${summary}.`,
        result: {
          id: data.attachment.id,
          label: data.attachment.label,
          url: data.attachment.url,
        },
      })
      setComposer({ mode: 'idle' })
      setValue('')
    },
    onError: handleCascadeError,
  })

  // Default-state list for /c Add customer: orphaned vehicles (no
  // customer linked). User taps one → composer enters target state
  // with the vehicle in tow, then types name+phone+email → atomic
  // create-customer + link in one shot.
  const orphanedEnabled =
    isLoaded &&
    composer.mode === 'verb' &&
    composer.verb === 'add_customer' &&
    debouncedQ.length === 0 &&
    dispatchResult === null
  const orphanedQuery = useQuery({
    queryKey: ['agentic-orphaned-vehicles'],
    enabled: orphanedEnabled,
    queryFn: () =>
      authFetch<{
        results: Array<SearchResult & { reason: string }>
      }>('/api/dashboard/orphaned-vehicles?limit=10', getToken),
    staleTime: 30_000,
  })

  // Add-customer-for-vehicle mutation — atomic create + link.
  const addCustomerForVehicle = useMutation({
    // Either creates a new customer + links (name + optional phone/
    // email) OR links an existing customer to the orphan vehicle
    // (customer_id, no name). The server endpoint branches on
    // customer_id presence.
    mutationFn: (
      payload:
        | {
            vehicle_id: string
            name: string
            phone: string | null
            email: string | null
          }
        | { vehicle_id: string; customer_id: string },
    ) =>
      authFetch<{
        ok: boolean
        customer: { id: string; name: string; url: string }
        linked_to_existing?: boolean
        request_id: string | null
      }>('/api/dashboard/add-customer-for-vehicle', getToken, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['agentic-orphaned-vehicles'] })
      queryClient.invalidateQueries({ queryKey: ['agentic-update-suggestions'] })
      const verb = data.linked_to_existing ? 'Linked' : 'Created'
      const tail = data.linked_to_existing ? 'to the vehicle' : 'and linked to the vehicle'
      settleDispatchResult({
        request_id: data.request_id ?? 'inline-link',
        mode: 'executed',
        intent: 'add_customer_for_vehicle',
        summary: `${verb} ${data.customer.name} ${tail}`,
        why_low_confidence: null,
        confidence: 1,
        args: {},
        reply: `${verb} ${data.customer.name} ${tail}.`,
        result: {
          id: data.customer.id,
          label: data.customer.name,
          url: data.customer.url,
        },
      })
      setComposer({ mode: 'idle' })
      setValue('')
    },
    onError: handleCascadeError,
  })

  // Existing-customer search for the "Add customer for vehicle" cascade.
  // Fires when the user is in that target state AND has typed ≥2 chars.
  // Surfaces near-matches ("marry" → "Mary X") so they can tap-link
  // instead of creating a duplicate. Mirrors the dedup contract the
  // brain's create_customer tool enforces server-side.
  const addCustomerSearchEnabled =
    isLoaded &&
    composer.mode === 'target' &&
    composer.verb === 'add_customer' &&
    composer.target.kind === 'vehicle' &&
    debouncedQ.length >= 2 &&
    dispatchResult === null
  const addCustomerSearchQuery = useQuery({
    queryKey: ['agentic-add-customer-search', debouncedQ],
    enabled: addCustomerSearchEnabled,
    queryFn: () =>
      authFetch<SearchResponse>(
        `/api/search?q=${encodeURIComponent(debouncedQ)}&kinds=customer&limit=5`,
        getToken,
      ),
    staleTime: 10_000,
  })

  // Default-state list for /u Update: recently-added customers and
  // vehicles that are missing critical fields. Most common Update
  // path is "I started this record earlier and need to finish it" —
  // so we surface those without making the user remember names.
  const updateSuggestEnabled =
    isLoaded &&
    composer.mode === 'verb' &&
    composer.verb === 'update' &&
    debouncedQ.length === 0 &&
    dispatchResult === null
  const updateSuggestQuery = useQuery({
    queryKey: ['agentic-update-suggestions'],
    enabled: updateSuggestEnabled,
    queryFn: () =>
      authFetch<{
        results: Array<
          SearchResult & {
            category:
              | 'mileage_after_appointment'
              | 'orphaned_vehicle'
              | 'missing_critical'
              | 'missing_optional'
            missing: string[]
            reason: string
            urgency: 'high' | 'medium'
            prefill_field: string | null
          }
        >
      }>('/api/dashboard/update-suggestions?limit=10', getToken),
    staleTime: 30_000,
  })

  // ─── Mutations ──────────────────────────────────────────────────────

  // Dock seam: hand committed actions to the host and CONSUME the
  // result. Clearing returns the composer to true idle — type-ahead
  // search and auto-stage gate on `dispatchResult === null`. The host
  // renders the committed action as a §4.1 receipt (ReceiptsHost), which
  // owns the whole Undo lifecycle (server-authoritative window, silent
  // expiry, mapUndoError inline failures) — the legacy interior banner +
  // its undo machinery were deleted in PR-14. Pattern A (render-time
  // snapshot swap) keeps the consume out of an effect; the host callback
  // fires from an effect, deduped by ref.
  const [dockReport, setDockReport] = useState<DispatchResponse | null>(null)
  if (dispatchResult?.mode === 'executed') {
    setDockReport(dispatchResult)
    setDispatchResult(null)
  }
  const dockReportedRef = useRef<DispatchResponse | null>(null)
  useEffect(() => {
    if (dockReport && dockReport !== dockReportedRef.current) {
      dockReportedRef.current = dockReport
      onExecuted(dockReport)
    }
  }, [dockReport, onExecuted])

  // Dashboard conversation lane STREAMS (Deploy #7). Instead of one buffered
  // JSON reply, /api/eric/dispatch/stream emits SSE: `tool` events (tool-progress
  // chips), `text` deltas (the reply as it generates), and a final `done`
  // envelope — the same DispatchResponse the buffered route returns — which lands
  // in dispatchResult so the result card + Undo pill render unchanged. We keep the
  // `dispatch.mutate(prompt)` / `dispatch.isPending` surface every call site uses.
  const [dispatchPending, setDispatchPending] = useState(false)
  // Hold-Send guard for a staged photo (locked 2026-06-10): Send waits ≤3s for
  // the attach-time classify; `sendHold` blocks double-entry during the hold,
  // and `stillReading` surfaces the calm "give it a second" hint when the hold
  // expires with the analysis still running.
  const [sendHold, setSendHold] = useState(false)
  const [stillReading, setStillReading] = useState(false)
  const [streamProgress, setStreamProgress] = useState<{
    tools: string[]
    text: string
  } | null>(null)

  const runDispatchStream = async (
    prompt: string,
    photo?: EricPhotoContext,
  ) => {
    // Supersede any in-flight stream + arm the unmount abort.
    dispatchAbortRef.current?.abort()
    const ac = new AbortController()
    dispatchAbortRef.current = ac
    const isCurrent = () => dispatchAbortRef.current === ac
    // The context generation this turn runs for — captured up front so the
    // executed-settle below consumes the RIGHT generation even if the user
    // started a new flow while the stream was in flight.
    const turnContextId = dispatchContextIdRef.current
    setDispatchPending(true)
    setStreamProgress({ tools: [], text: '' })
    setDispatchFailed(false)
    setIncompleteReply(null)
    setLastDispatch({ prompt, photo })
    // A new turn supersedes any prior reply-card customer picker — clear its
    // typed-but-not-picked search text so it can't pre-fill the next picker.
    setReplyCustomerQ('')
    setReplyCustomerDebounced('')
    // A new turn also supersedes any pending staged attachment from a PRIOR
    // upload — its chips belong to that photo, not this turn. (The Send/Ask-Eric
    // path already consumed + cleared the staged photo before calling here, so
    // this only clears a staged photo the user is ignoring by typing a fresh
    // turn instead.)
    stagedAttachment.clear()
    let final: DispatchResponse | null = null
    try {
      const token = await getToken()
      if (!token) throw new Error('missing_token')
      const res = await fetch('/api/eric/dispatch/stream', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
        },
        body: JSON.stringify({
          prompt,
          conversation_id: conversationId,
          ...(photo ? { photo } : {}),
        }),
        signal: ac.signal,
      })
      // Pre-stream failures (rate_limit / duplicate / 5xx) come back as JSON,
      // not SSE — mirror the old mutation's silent reset (the user just retries).
      if (!res.ok || !res.body) {
        throw new Error(`dispatch_stream_${res.status}`)
      }
      const reader = res.body.getReader()
      const decoder = new TextDecoder()
      let buf = ''
      readLoop: for (;;) {
        const { value, done } = await reader.read()
        if (done) break
        buf += decoder.decode(value, { stream: true })
        const frames = buf.split('\n\n')
        buf = frames.pop() ?? ''
        for (const frame of frames) {
          const dataLine = frame.split('\n').find((l) => l.startsWith('data:'))
          if (!dataLine) continue
          const payload = dataLine.slice(5).trim()
          if (!payload) continue
          let evt: {
            t?: string
            name?: string
            delta?: string
            envelope?: DispatchResponse
            message?: string
          }
          try {
            evt = JSON.parse(payload)
          } catch {
            continue
          }
          if (evt.t === 'tool' && evt.name) {
            const name = evt.name
            setStreamProgress((p) =>
              p ? { ...p, tools: [...p.tools, name] } : { tools: [name], text: '' },
            )
          } else if (evt.t === 'text' && evt.delta) {
            const delta = evt.delta
            setStreamProgress((p) =>
              p ? { ...p, text: p.text + delta } : { tools: [], text: delta },
            )
          } else if (evt.t === 'done' && evt.envelope) {
            final = evt.envelope
            break readLoop
          } else if (evt.t === 'error') {
            // Soft transport marker — a `done` envelope still follows.
            console.error(
              JSON.stringify({
                event: 'task_eric_input.stream_error',
                message: evt.message,
              }),
            )
          }
        }
      }
    } catch (err) {
      // Intentional abort (unmount / superseded) — leave state to the winner.
      if ((err as { name?: string } | null)?.name === 'AbortError') return
      console.error(
        JSON.stringify({
          event: 'task_eric_input.dispatch_stream_failed',
          error: err instanceof Error ? err.message : String(err),
        }),
      )
      if (isCurrent()) {
        // Transport fault — show the retry card instead of a silent blank.
        setDispatchResult(null)
        setDispatchFailed(true)
        setSlotTapPending(false)
        setDispatchPending(false)
        setStreamProgress(null)
      }
      return
    }
    // Settle off the final envelope — mirrors the old onSuccess behavior. Skip if
    // a newer dispatch superseded this one (guards against stale-state clobber).
    if (!isCurrent()) return
    // A `reply`-mode envelope with no reply text is the blank-card symptom: the
    // turn was allowed but produced nothing the server fallback could recover.
    // Treat it like any other failure → the retry card, never a blank.
    // Part C: `incomplete` marks a turn whose reply is the server's honest
    // "didn't finish" constant — non-empty text, but the turn did NOT complete.
    const finalIsBlank =
      !final ||
      final.incomplete === true ||
      (final.mode === 'reply' && (final.reply ?? '').trim() === '')
    if (final && !finalIsBlank) {
      setDispatchResult(final)
      setValue('')
      // Capture the customer/vehicle Eric resolved this turn so a VIN photo
      // uploaded next attaches to them. Sticky: a focusless turn leaves the
      // prior focus intact (the brain's memory still holds the customer; our
      // tool-trail derivation just had nothing new to report this turn).
      if (final.focus) setConversationFocus(final.focus)
      if (final.mode === 'executed') {
        // A write settled via the brain — consume this turn's slot-picker
        // context so a stale "Tap a slot to book" strip can't re-book.
        setConsumedContextId((prev) => consumeContextId(prev, turnContextId))
        queryClient.invalidateQueries({ queryKey: ['agentic-load'] })
        queryClient.invalidateQueries({ queryKey: ['agentic-needs-action'] })
        queryClient.invalidateQueries({ queryKey: ['agentic-saved', 'today'] })
      }
      // A reply that re-asks for the customer DISOWNS any pinned reply-card
      // pick — the pin was supposed to resolve exactly that question. Clear
      // it so the customer picker re-surfaces (instead of the slot strip)
      // and a slot tap can't fast-lane book the stale pin against a reply
      // that says the customer is unresolved. The route helper then sees
      // customer=null → brain (see reply-card-booking.test.ts).
      if (final.mode === 'reply' && replyAsksForCustomer(final.reply ?? '')) {
        setLastDispatchContext((prev) =>
          prev.customer ? { ...prev, customer: null } : prev,
        )
      }
      setComposer({ mode: 'idle' })
    } else {
      setDispatchResult(null)
      setDispatchFailed(true)
      // Part C: surface the server's honest reply (esp. the mutating "check the
      // record before retrying" warning) in the retry card; generic copy otherwise.
      setIncompleteReply(
        final?.incomplete && final.reply?.trim() ? final.reply.trim() : null,
      )
    }
    setSlotTapPending(false)
    setDispatchPending(false)
    setStreamProgress(null)
  }

  const dispatch = {
    mutate: (prompt: string) => {
      void runDispatchStream(prompt)
    },
    isPending: dispatchPending,
  }

  // Re-run the exact turn that failed (Fix 4 retry). Safe to repeat — Eric leads
  // with lookup_customer and the tools dedup, so a retry doesn't double-write.
  const retryLastDispatch = () => {
    if (!lastDispatch || dispatch.isPending) return
    setDispatchFailed(false)
    setIncompleteReply(null)
    void runDispatchStream(lastDispatch.prompt, lastDispatch.photo)
  }
  const dismissDispatchFailure = () => {
    setDispatchFailed(false)
    setIncompleteReply(null)
    setLastDispatch(null)
  }

  // Auto-stage cascade from freeform typing. When the user types a
  // clean "book/reschedule <name>" pattern from idle and the search
  // dropdown surfaces exactly one matching customer/appointment, lift
  // them into the cascade target state so the slot strip is already
  // waiting below. They can peel back with Backspace if the auto-pick
  // was wrong. Skipped when the prompt also has a clock-time — those
  // head for the brain fast-lane on Send (direct booking via CASCADE
  // directive, no slot tap needed). Also skipped while a photo is
  // staged: the typed text is the photo's CAPTION (Send routes
  // text+photo to the brain together), so the lift's discard-the-
  // sentence trade-off would wipe a draft the user still needs — the
  // A3 draft-wipe bug. The same caption must also survive clearing the
  // staged photo from the chip X to swap it (the detachedCaption
  // snapshot below — wipe-on-detach residual). Derivation lives in
  // src/lib/auto-stage.ts so the gating rules are unit-testable.
  //
  // Pattern A (You Might Not Need an Effect): derive the would-be
  // auto-stage target during render, then use a prev-snapshot guard so
  // the actual setComposer + setValue calls only fire once per unique
  // (verb, target id) pair. Avoids the react-hooks/set-state-in-effect
  // lint rule without changing the observable behavior — the user still
  // sees the composer advance into target mode the moment the search
  // results settle on a single match for a clean book/reschedule prompt.
  // Composer value snapshotted when the staged photo is cleared from the
  // AttachmentChip X (wipe-on-detach residual): with the photo gone the
  // staged gate lifts while the caption — and the search results already
  // settled on it — are unchanged, so the lift would re-fire and wipe
  // the caption the user kept to swap the photo. The snapshot suppresses
  // the lift until the text actually changes; the Pattern-A reset below
  // re-arms it the moment the user edits the caption.
  const [detachedCaption, setDetachedCaption] = useState<string | null>(null)
  if (detachedCaption !== null && value !== detachedCaption) {
    setDetachedCaption(null)
  }
  const autoStageCandidate = computeAutoStageCandidate({
    composerOpen: composer.mode === 'idle' || composer.mode === 'freeform',
    dispatchPending: dispatch.isPending,
    hasStagedAttachment: staged !== null,
    detachedCaption,
    intent: parseFreeformIntent(value),
    value,
    broadResults: searchQuery.data?.results ?? [],
    fallbackResults: nameOnlySearchQuery.data?.results ?? [],
  })
  // Signature of the candidate the effect-replacement has already
  // acted on. Stays null until the first auto-stage fires, then tracks
  // the most-recent (verb, target id) pair so the same candidate
  // doesn't re-fire across renders.
  const [autoStagedSig, setAutoStagedSig] = useState<string | null>(null)
  const candidateSig = autoStageCandidate
    ? `${autoStageCandidate.verb}:${autoStageCandidate.target.id}`
    : null
  if (candidateSig !== null && candidateSig !== autoStagedSig) {
    setAutoStagedSig(candidateSig)
    setComposer({
      mode: 'target',
      verb: autoStageCandidate!.verb,
      target: autoStageCandidate!.target,
    })
    // Clear the value — the rest of their sentence ("for wednesday")
    // is lost on purpose; the cascade slot strip is the prize, and
    // any job description gets typed fresh in the now-empty
    // JOB DESCRIPTION field (or auto-prefilled from booking-
    // suggestions if a carryover exists for the picked customer).
    setValue('')
  } else if (candidateSig === null && autoStagedSig !== null) {
    // User peeled back / cleared the field. Reset the signature so a
    // future re-match (e.g. they type the same prompt again) can stage
    // a fresh time.
    setAutoStagedSig(null)
  }

  const updateField = useMutation({
    mutationFn: (payload: {
      kind: string
      id: string
      field: string
      value: string
    }) =>
      authFetch<{
        ok: boolean
        label: string
        url: string
        kind: string
        id: string
        request_id: string | null
      }>('/api/dashboard/update-field', getToken, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['agentic-load'] })
      queryClient.invalidateQueries({ queryKey: ['agentic-needs-action'] })
      // Synthesize a DispatchResponse-shaped success so the same green
      // banner renders. Carries the server's real request_id so the
      // Undo button maps to a real assistant_requests row.
      settleDispatchResult({
        request_id: data.request_id ?? 'inline-update',
        mode: 'executed',
        intent: 'update_field',
        summary: `Updated ${data.label}`,
        why_low_confidence: null,
        confidence: 1,
        args: {},
        reply: `Updated ${data.label}.`,
        result: { id: data.id, label: data.label, url: data.url },
      })
      setValue('')
      setComposer({ mode: 'idle' })
    },
    onError: handleCascadeError,
  })

  // ─── Submit ─────────────────────────────────────────────────────────

  const onSubmit = () => {
    const trimmed = value.trim()

    // `report:` — log a problem instead of sending to Eric. Goes straight to the
    // freeform dispatch (the server backstop diverts + saves it), bypassing every
    // cascade branch below so a staged target/photo can't intercept a report.
    if (reportMode) {
      if (dispatch.isPending) return
      setValue('')
      void runDispatchStream(trimmed)
      return
    }

    // Any prior 403 should clear on a fresh submission — the user may
    // have switched verbs or fixed the request.
    setCascadeError(null)

    // A staged photo + Send → ship the photo's classify-lane EXTRACTION to
    // Eric (pixels never ship — locked 2026-06-10). consumeForSend holds ≤3s
    // for an in-flight analysis; `unreadable` / `pending` keep the photo
    // staged (the error chip / still-reading hint explain) and nothing
    // dispatches. This wins over every cascade branch below — a staged image
    // is an explicit "send this to Eric" intent.
    if (stagedAttachment.staged) {
      if (sendHold) return
      setSendHold(true)
      void stagedAttachment.consumeForSend().then((res) => {
        setSendHold(false)
        if (res.kind === 'ready') {
          setStillReading(false)
          void runDispatchStream(trimmed, res.photo)
        } else if (res.kind === 'pending') {
          setStillReading(true)
        }
      })
      return
    }

    // /b book or /m reschedule with a slot staged = a fully-resolved
    // cascade → autoflo FAST LANE, bypassing Eric (Dev Tracker #5). The
    // mechanic has already pinned the exact target + slot, and the
    // /dashboard/book + /dashboard/reschedule endpoints now run the same
    // deterministic checks the brain used to (slot-overlap + carryover —
    // returned as advisories, never blocking). So we skip the ~3-6s brain
    // round-trip and the memory pool entirely. Anything that isn't a clean
    // book/reschedule falls through to the brain below.
    if (composer.mode === 'target_with_slot') {
      if (composer.verb === 'book' && composer.target.kind === 'customer') {
        // ANY successful booking consumes the live slot-picker context so a
        // lingering reply-card strip can't double-book. Capture the id at
        // call time — a generation started after this lands stays tappable.
        const ctxId = dispatchContextIdRef.current
        book.mutate(
          {
            customer_id: composer.target.id,
            starts_at_iso: composer.slot.starts_at_iso,
            notes: trimmed.length > 0 ? trimmed : null,
          },
          {
            onSuccess: () =>
              setConsumedContextId((prev) => consumeContextId(prev, ctxId)),
          },
        )
        return
      }
      if (
        composer.verb === 'reschedule' &&
        composer.target.kind === 'appointment'
      ) {
        const ctxId = dispatchContextIdRef.current
        reschedule.mutate(
          {
            appointment_id: composer.target.id,
            new_starts_at_iso: composer.slot.starts_at_iso,
          },
          {
            onSuccess: () =>
              setConsumedContextId((prev) => consumeContextId(prev, ctxId)),
          },
        )
        return
      }
      // Unexpected verb/target pairing with a slot → let the brain handle it.
      const prefix = composeCascadePrefix(
        composer.verb,
        composer.target,
        composer.slot,
      )
      const prompt = `${prefix} ${trimmed}`.trim()
      startDispatchContext({
        verb: composer.verb,
        customer:
          composer.target.kind === 'customer' ? composer.target : null,
        vehicle:
          composer.target.kind === 'vehicle' ? composer.target : null,
        appointment:
          composer.target.kind === 'appointment' ? composer.target : null,
        note: trimmed.length > 0 ? trimmed : null,
      })
      dispatch.mutate(prompt)
      return
    }

    // /u + target + field selected → structured update endpoint (no
    // classifier round-trip — exact field is known).
    if (composer.mode === 'field') {
      if (trimmed.length === 0) return
      updateField.mutate({
        kind: composer.target.kind,
        id: composer.target.id,
        field: composer.field.key,
        value: trimmed,
      })
      return
    }

    // /v Add vehicle + customer-without-vehicles picked → atomic
    // create+link via the structured endpoint. The freeform value is
    // parsed client-side into year/make/model/vin.
    if (
      composer.mode === 'target' &&
      composer.verb === 'add_vehicle' &&
      composer.target.kind === 'customer'
    ) {
      if (trimmed.length === 0) return
      const parsed = parseVehicleInput(trimmed)
      if (!parsed.year && !parsed.make && !parsed.model && !parsed.vin) return
      addVehicleForCustomer.mutate({
        customer_id: composer.target.id,
        year: parsed.year,
        make: parsed.make,
        model: parsed.model,
        vin: parsed.vin,
      })
      return
    }

    // /c Add customer + orphaned vehicle picked → atomic create+link
    // via the structured endpoint. The freeform value is parsed
    // client-side into name/phone/email.
    if (
      composer.mode === 'target' &&
      composer.verb === 'add_customer' &&
      composer.target.kind === 'vehicle'
    ) {
      if (trimmed.length === 0) return
      const parsed = parseCustomerInput(trimmed)
      if (!parsed.name) return
      addCustomerForVehicle.mutate({
        vehicle_id: composer.target.id,
        name: parsed.name,
        phone: parsed.phone,
        email: parsed.email,
      })
      return
    }

    // Verb-active state with a target picked but no slot yet → brain
    // gets a [CASCADE: verb=... target=...] block + the user's note.
    // Brain has full context (no re-lookup), asks at most one
    // clarifying question if a required field is missing.
    if (composer.mode === 'target') {
      const prefix = composeCascadePrefix(composer.verb, composer.target, null)
      const prompt = `${prefix} ${trimmed}`.trim()
      if (prompt.length === 0) return
      startDispatchContext({
        verb: composer.verb,
        customer:
          composer.target.kind === 'customer' ? composer.target : null,
        vehicle:
          composer.target.kind === 'vehicle' ? composer.target : null,
        appointment:
          composer.target.kind === 'appointment' ? composer.target : null,
        note: trimmed.length > 0 ? trimmed : null,
      })
      dispatch.mutate(prompt)
      return
    }

    // Add-* verbs use freeform compose against the classifier.
    if (composer.mode === 'verb') {
      const prefix = composeAddPrefix(composer.verb)
      const prompt = prefix ? `${prefix} ${trimmed}`.trim() : trimmed
      if (prompt.length === 0) return
      startDispatchContext({
        verb: composer.verb,
        customer: null,
        vehicle: null,
        appointment: null,
        note: trimmed.length > 0 ? trimmed : null,
      })
      dispatch.mutate(prompt)
      return
    }

    // Idle / freeform → straight to the brain. Sniff the prompt for a
    // book/reschedule intent so the reply card can still render the
    // slot strip when Eric asks "what time?". target/customer/etc.
    // stay null — the brain's session memory carries the customer
    // from the previous turn for the slot tap re-dispatch.
    if (trimmed.length === 0 || dispatch.isPending) return
    startDispatchContext({
      verb: parseFreeformIntent(trimmed),
      customer: null,
      vehicle: null,
      appointment: null,
      note: trimmed,
    })
    dispatch.mutate(trimmed)
  }

  // (Enter-submits / Backspace-peels / mic / paperclip all live in the
  // shared ChatInput now — submitOnEnter, onBackspaceEmpty, the voice
  // slot and the attachment slot. See the <ChatInput> mount below.)

  // ─── Staged-attachment helpers ──────────────────────────────────────
  // Server backstop for the conversational customer when client focus is gone
  // (page reload / deep link). Mirrors VehicleFromPhoto's fallback.
  const fetchDashboardFocus = async (): Promise<EricFocus | null> => {
    try {
      const token = await getToken()
      if (!token) return null
      const res = await fetch('/api/eric/dashboard-focus', {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) return null
      const data = (await res.json()) as { focus: EricFocus | null }
      return data.focus ?? null
    } catch {
      return null
    }
  }
  // The shop's most-recently-active customer (server-side, last ~30 min), used
  // to backstop the create/receipt chips when a photo arrives with no focus.
  // Returns null when nobody's been touched recently.
  const fetchRecentCustomer = async (): Promise<{
    id: string
    name: string
  } | null> => {
    try {
      const token = await getToken()
      if (!token) return null
      const res = await fetch('/api/dashboard/recent-customer', {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) return null
      const data = (await res.json()) as {
        customer: { id: string; name: string } | null
      }
      return data.customer ?? null
    } catch {
      return null
    }
  }
  // Attach a file: STAGE it in the composer ALWAYS (no instant-fire, no 0-click
  // create). The thumbnail lands immediately, the classify runs in the
  // background, and content-aware chips appear on the result. Nothing writes
  // until the user taps a chip or hits Send.
  const onAttachFile = async (file: File) => {
    if (!file) return
    // Reset the hold hint so a stale "still reading" from a prior photo's expired
    // hold can't flash over this fresh photo's normal analyzing phase.
    setStillReading(false)
    stagedAttachment.stage(file)
    // (ChatInput's attachment slot resets its own file input after the pick.)
    // Prime the focus + recent-customer context the chips key off. The brain's
    // tool-trail focus is sticky; the recent-customer fetch backstops a cold,
    // no-focus VIN/receipt so the lead chip can still name a real owner.
    if (!conversationFocus) {
      const f = await fetchDashboardFocus()
      if (f) setConversationFocus(f)
      if (!f?.customerId) {
        const rc = await fetchRecentCustomer()
        setRecentCustomer(rc)
      }
    }
  }
  // The staged-attachment chip set, derived from the classification + the
  // surrounding conversation. In an active conversation (a resolved customer in
  // focus OR a standing Eric reply this session) we LEAD with "Ask Eric" so the
  // brain decides create-vs-update an existing vehicle (avoids a dup); cold, the
  // deterministic create-chip leads. Re-derived on every render — pure + cheap.
  const isInConversation = Boolean(
    dispatchResult || conversationFocus?.customerId,
  )
  // Memoized so the array identity is stable across renders — the suggestion
  // row resets its inline picker when this reference changes (a new photo), so
  // an unstable identity would defeat the picker by re-rendering it away.
  const stagedClassification = staged?.classification ?? null
  const stagedDecodedVin = staged?.decodedVin ?? null
  const stagedReady = staged?.status === 'ready'
  // A boolean (not raw `value`) so the memo identity flips only at the
  // empty↔non-empty boundary — depending on `value` directly would recompute the
  // suggestions (and reset the inline picker) on every keystroke.
  const composerHasComposeText = value.trim().length > 0
  const stagedSuggestions: AttachmentSuggestion[] = useMemo(
    () =>
      stagedReady
        ? buildSuggestions(stagedClassification, stagedDecodedVin, {
            focus: conversationFocus,
            recentCustomer,
            isInConversation,
            composerHasComposeText,
          })
        : [],
    [
      stagedReady,
      stagedClassification,
      stagedDecodedVin,
      conversationFocus,
      recentCustomer,
      isInConversation,
      composerHasComposeText,
    ],
  )

  // Send the staged photo to Eric: consume its classify-lane extraction (the
  // chips only render on `ready`, so the hold has already settled here) and
  // dispatch it as a <photo_analysis> context turn — the brain acts on the
  // extraction against its working-memory context; pixels never ship.
  const sendStagedToEric = async (caption: string) => {
    if (sendHold) return
    setSendHold(true)
    const res = await stagedAttachment.consumeForSend()
    setSendHold(false)
    if (res.kind === 'ready') {
      setStillReading(false)
      void runDispatchStream(caption, res.photo)
    } else if (res.kind === 'pending') {
      setStillReading(true)
    }
  }

  const attachBusy =
    addVehicleForCustomer.isPending ||
    logMileageForPhoto.isPending ||
    saveAttachment.isPending

  // Everything that makes Send a no-op — fed to ChatInput as `submitting`
  // so the button spinner, the Enter guard and the input lock stay in
  // sync. Same union the legacy hand-rolled Send button disabled on.
  const submitBusy =
    dispatch.isPending ||
    sendHold ||
    updateField.isPending ||
    book.isPending ||
    reschedule.isPending ||
    addVehicleForCustomer.isPending ||
    addCustomerForVehicle.isPending ||
    cancelAppointment.isPending ||
    logMileageForPhoto.isPending ||
    saveAttachment.isPending

  // ChatInput onChange glue: slash shortcuts + the idle↔freeform
  // transitions ride the value change, exactly as the legacy textarea's
  // onChange did (synchronous, no effect bounce).
  const onComposerChange = (next: string) => {
    // Slash shortcut: typed `/u ` in idle/freeform becomes the matching
    // Update / Book / etc. verb.
    if (composer.mode === 'idle' || composer.mode === 'freeform') {
      const m = next.match(/^(\/[a-z])(?:\s|$)/)
      if (m) {
        const verb = SLASH_TO_VERB[m[1]] as Verb | undefined
        if (verb) {
          setValue('')
          startVerb(verb)
          return
        }
      }
    }
    setValue(next)
    if (composer.mode === 'idle' && next.length > 0)
      setComposer({ mode: 'freeform' })
    if (composer.mode === 'freeform' && next.length === 0)
      setComposer({ mode: 'idle' })
  }

  // Chip handlers — each clears the staged photo, then fires the matching
  // undoable mutation (or hands off to Eric). Mirror the old smart-attach
  // helpers, now triggered by content-aware chips instead of the branching
  // classify state machine.
  const attachHandlers: AttachmentSuggestionHandlers = {
    onAddVehicle: (s) => {
      stagedAttachment.clear()
      addVehicleForCustomer.mutate({
        customer_id: s.customerId,
        year: s.year,
        make: s.make,
        model: s.model,
        vin: s.vin,
      })
    },
    onAddVehicleForPickedCustomer: (s, customer) => {
      if (customer.kind !== 'customer') return
      stagedAttachment.clear()
      addVehicleForCustomer.mutate({
        customer_id: customer.id,
        year: s.year,
        make: s.make,
        model: s.model,
        vin: s.vin,
      })
    },
    onSaveOrphan: (s) => {
      stagedAttachment.clear()
      addVehicleForCustomer.mutate({
        customer_id: null,
        year: s.year,
        make: s.make,
        model: s.model,
        vin: s.vin,
      })
    },
    onLogMileage: (s) => {
      if (!s.vehicleId) return
      stagedAttachment.clear()
      logMileageForPhoto.mutate({
        vehicle_id: s.vehicleId,
        mileage_km: s.mileageKm,
        photo_r2_key: s.photoR2Key,
      })
    },
    onLogMileageForPickedVehicle: (s, vehicle) => {
      if (vehicle.kind !== 'vehicle') return
      stagedAttachment.clear()
      logMileageForPhoto.mutate({
        vehicle_id: vehicle.id,
        mileage_km: s.mileageKm,
        photo_r2_key: s.photoR2Key,
      })
    },
    onSaveReceipt: (s) => {
      stagedAttachment.clear()
      saveAttachment.mutate({
        photo_r2_key: s.photoR2Key,
        customer_id: s.customerId,
        total_cents: s.totalCents,
        currency: s.currency,
      })
    },
    onAskEric: () => {
      void sendStagedToEric(value.trim())
    },
  }

  // ─── Render helpers ─────────────────────────────────────────────────

  const placeholder = (() => {
    if (composer.mode === 'idle' || composer.mode === 'freeform')
      return t('agentic.input.placeholder')
    if (composer.mode === 'verb') {
      const kinds = VERB_SEARCH_KINDS[composer.verb]
      if (kinds === null) return t(`agentic.compose.placeholder.${composer.verb}`)
      // Map the comma-separated kinds string to a grammatically-aware
      // search-for prompt. Avoids "a appointment" and gives the
      // multi-kind /u case a clean phrasing.
      if (kinds === 'customer') return t('agentic.compose.search_for.customer')
      if (kinds === 'vehicle') return t('agentic.compose.search_for.vehicle')
      if (kinds === 'appointment')
        return t('agentic.compose.search_for.appointment')
      if (kinds === 'customer,vehicle')
        return t('agentic.compose.search_for.customer_vehicle')
      return t('agentic.compose.placeholder.search', {
        what: t(`agentic.compose.what.${kinds.split(',')[0]}`),
      })
    }
    if (composer.mode === 'target' || composer.mode === 'target_with_slot') {
      if (
        composer.verb === 'add_customer' &&
        composer.target.kind === 'vehicle'
      ) {
        return t('agentic.compose.placeholder.add_customer_for_vehicle', {
          target: composer.target.name,
        })
      }
      if (
        composer.verb === 'add_vehicle' &&
        composer.target.kind === 'customer'
      ) {
        return t('agentic.compose.placeholder.add_vehicle_for_customer', {
          target: composer.target.name,
        })
      }
      return t(`agentic.compose.placeholder.${composer.verb}_after`, {
        target: composer.target.name,
      })
    }
    if (composer.mode === 'field') {
      return t(composer.field.placeholderKey, { target: composer.target.name })
    }
    return ''
  })()

  const submitLabel = (() => {
    if (reportMode) return t('home_v2.report_send')
    if (composer.mode === 'field') return t('agentic.compose.save_change')
    return t('agentic.input.send')
  })()

  // What's-this-input-for label + help line. Renders between the pill
  // row and the textarea so Emil knows the field's role at a glance
  // (the pill row alone doesn't say "what comes next is the job
  // description"). Returns null when there's no special context (idle
  // / freeform — the placeholder is enough on its own).
  const contextLabel = (() => {
    if (
      (composer.mode === 'target' || composer.mode === 'target_with_slot') &&
      composer.verb === 'book'
    ) {
      return {
        label: t('agentic.compose.field_label.job_description'),
        // While Eric's note edit is pending, the diff tile replaces the
        // textarea — "Edit before sending" would point at a control
        // that isn't there.
        help: haikuSuggestion
          ? t('agentic.compose.field_help.job_description_pending')
          : t('agentic.compose.field_help.job_description'),
      }
    }
    if (
      (composer.mode === 'target' || composer.mode === 'target_with_slot') &&
      composer.verb === 'reschedule'
    ) {
      return {
        label: t('agentic.compose.field_label.new_time'),
        help: t('agentic.compose.field_help.new_time'),
      }
    }
    if (
      composer.mode === 'target' &&
      composer.verb === 'add_customer' &&
      composer.target.kind === 'vehicle'
    ) {
      return {
        label: t('agentic.compose.field_label.customer_details'),
        help: t('agentic.compose.field_help.customer_details'),
      }
    }
    if (
      composer.mode === 'target' &&
      composer.verb === 'add_vehicle' &&
      composer.target.kind === 'customer'
    ) {
      return {
        label: t('agentic.compose.field_label.vehicle_details'),
        help: t('agentic.compose.field_help.vehicle_details'),
      }
    }
    if (composer.mode === 'field') {
      return {
        label: t(composer.field.labelKey),
        help: t('agentic.compose.field_help.update_value'),
      }
    }
    return null
  })()

  const showSearch =
    dispatchResult === null &&
    // Don't show the "0 RESULTS / No matches" search panel while a dispatch
    // is in flight — the typed command stays in the box, so an un-gated
    // search renders a false-failure panel stacked under "Eric is working…".
    !dispatchPending &&
    (composer.mode === 'idle' || composer.mode === 'freeform' || composer.mode === 'verb') &&
    // >= 2 matches `searchEnabled` (the query threshold) so the panel never
    // shows interactive rows at 1 char before the query has even run.
    debouncedQ.length >= 2 &&
    searchKinds !== null

  // Keep the "Start with" chips visible on a post-action executed
  // settle (the receipt reports a completed action; it shouldn't
  // suppress the shortcuts — and the executed result is consumed into
  // the host receipt within the same render pass anyway). Still hidden
  // for the mid-conversation `reply`/`preview` cards (those own the
  // composer's attention with their own slot pickers / follow-ups).
  const showVerbChips =
    composer.mode === 'idle' &&
    value.length === 0 &&
    (dispatchResult === null || dispatchResult.mode === 'executed')

  const showFieldChips = composer.mode === 'target' && composer.verb === 'update'
  const fieldChipSet =
    composer.mode === 'target' && composer.verb === 'update'
      ? composer.target.kind === 'customer'
        ? CUSTOMER_FIELDS
        : composer.target.kind === 'vehicle'
          ? VEHICLE_FIELDS
          : []
      : []

  return (
    <div className="space-y-3">
      {/* The composer's own hero. Hidden when a host surface already
          provides a page header — the shell-level CommandDock (the only
          live mount since PR-4) always does, so this renders only for
          hypothetical future undocked mounts. */}
      {!hideHero ? (
        <header className="text-center">
          <h1 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
            {t('agentic.hero.title')}
          </h1>
          <p className="mt-1 whitespace-nowrap text-sm text-muted-foreground sm:text-base">
            {t('agentic.hero.tagline')}
          </p>
        </header>
      ) : null}

      {/* Composer chrome: breadcrumb / staged photo / field-role label /
          report hint stack above the shared ChatInput. The card-level
          focus ring went with the borderless legacy textarea — the
          ChatInput textarea carries its own focus ring now. */}
      <div className="rounded-2xl border border-border bg-card shadow-sm">
        {/* Breadcrumb pill — only visible when the composer has progressed
            past `idle`. Each step is removable via its × button (or
            Backspace from an empty input). */}
        {composer.mode !== 'idle' && composer.mode !== 'freeform' ? (
          <div className="flex flex-wrap items-center gap-1 border-b border-border px-3 py-2">
            <ComposerPill
              label={t(VERB_LABEL_KEY[
                composer.mode === 'field' ? 'update' : composer.verb
              ])}
              onRemove={peelBack}
            />
            {composer.mode === 'target' ||
            composer.mode === 'target_with_slot' ||
            composer.mode === 'field' ? (
              <>
                <Caret />
                <ComposerPill
                  label={composer.target.name}
                  onRemove={peelBack}
                />
              </>
            ) : null}
            {composer.mode === 'target_with_slot' ? (
              <>
                <Caret />
                <ComposerPill
                  label={composer.slot.label}
                  onRemove={peelBack}
                />
              </>
            ) : null}
            {composer.mode === 'field' ? (
              <>
                <Caret />
                <ComposerPill
                  label={t(composer.field.labelKey)}
                  onRemove={peelBack}
                />
              </>
            ) : null}
          </div>
        ) : null}

        {/* Staged attachment — the photo sits in the composer as a thumbnail
            while it classifies in the background; content-aware action chips
            appear on the result. Nothing auto-acts: the user taps a chip or
            hits Send (which hands the photo to Eric). Replaces the old
            amber SUGGEST / no-focus-offer cards. */}
        {staged ? (
          <div className="border-b border-border px-3 py-2.5">
            <AttachmentChip
              staged={staged}
              onClear={() => {
                setStillReading(false)
                // Wipe-on-detach residual: snapshot the caption so the
                // auto-stage lift can't re-fire on the unchanged text the
                // moment the staged gate opens (see the detachedCaption
                // block above).
                setDetachedCaption(value)
                stagedAttachment.clear()
              }}
            />
            {/* Hold-Send expired with the analysis still running — calm hint,
                self-hides once the classify settles or the photo clears.
                Suppressed while a note suggestion is pending: "Send again"
                would reference the button the suggestion row replaced. */}
            {stillReading && staged.status === 'analyzing' && !haikuSuggestion ? (
              <p className="mt-1.5 text-xs text-muted-foreground" aria-live="polite">
                {t('agentic.attach.staged.still_reading')}
              </p>
            ) : null}
            {staged.status === 'ready' && stagedSuggestions.length > 0 ? (
              <AttachmentSuggestions
                suggestions={stagedSuggestions}
                handlers={attachHandlers}
                busy={attachBusy}
              />
            ) : null}
          </div>
        ) : null}

        {/* Field-role label — tells Emil what the next input is for. */}
        {contextLabel ? (
          <div className="border-b border-border bg-muted/30 px-4 pb-1.5 pt-2">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-brand">
              {contextLabel.label}
            </div>
            <div className="text-[11px] text-muted-foreground">
              {contextLabel.help}
            </div>
          </div>
        ) : null}

        {/* Report mode — the message starts with `report:`, so it logs a problem
            instead of going to Eric. Calm, not an error. */}
        {reportMode ? (
          <div
            className="flex items-center gap-1.5 border-b border-border bg-muted/30 px-4 pb-1.5 pt-2 text-xs text-muted-foreground animate-in fade-in slide-in-from-bottom-1 duration-150 ease-out motion-reduce:animate-none"
            aria-live="polite"
          >
            <Flag className="size-3 shrink-0" />
            <span>{t('home_v2.report_mode_hint')}</span>
          </div>
        ) : null}

        {/* Haiku note-suggestion pending: the diff preview + its pick
            chips replace the whole input row (same as pre-strangle —
            the textarea AND the button row swap out together). */}
        {haikuSuggestion ? (
          <>
            <DiffPreview original={value} suggestion={haikuSuggestion} />
            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border px-2 py-2">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-brand">
                {t('agentic.suggestion.cleaner_label')}
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                {modifyChoosing ? (
                  <>
                    <Button
                      type="button"
                      onClick={() => {
                        // Edit MINE: keep current value as-is, dismiss
                        // the suggestion, give the user a normal
                        // editable textarea.
                        setHaikuSuggestion(null)
                        setModifyChoosing(false)
                        requestAnimationFrame(() =>
                          chatInputRef.current?.focus(),
                        )
                      }}
                      variant="chip"
                      size="pill-chip"
                      className="text-sm font-normal"
                    >
                      {t('agentic.suggestion.edit_mine')}
                    </Button>
                    <Button
                      type="button"
                      onClick={() => {
                        // Edit ERIC'S: apply the suggestion, dismiss
                        // the diff, drop the user into the textarea to
                        // tweak it.
                        setValue(haikuSuggestion)
                        setHaikuSuggestion(null)
                        setModifyChoosing(false)
                        requestAnimationFrame(() =>
                          chatInputRef.current?.focus(),
                        )
                      }}
                      variant="chip"
                      size="pill-chip"
                      className="text-sm font-normal"
                    >
                      {t('agentic.suggestion.edit_eric')}
                    </Button>
                    <Button
                      type="button"
                      onClick={() => setModifyChoosing(false)}
                      variant="ghost"
                      size="pill-chip"
                      className="px-3 text-sm font-normal text-muted-foreground hover:text-foreground"
                    >
                      {t('agentic.suggestion.cancel_modify')}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      type="button"
                      onClick={() => {
                        setValue(haikuSuggestion)
                        setHaikuSuggestion(null)
                      }}
                      variant="default"
                      size="pill-chip"
                      className="bg-brand px-4 text-sm font-medium text-white hover:bg-brand-dark"
                    >
                      {t('agentic.suggestion.use_this')}
                    </Button>
                    <Button
                      type="button"
                      onClick={() => setModifyChoosing(true)}
                      variant="chip"
                      size="pill-chip"
                      className="text-sm font-normal"
                    >
                      {t('agentic.suggestion.modify')}
                    </Button>
                    <Button
                      type="button"
                      onClick={() => setHaikuSuggestion(null)}
                      variant="ghost"
                      size="pill-chip"
                      className="px-3 text-sm font-normal text-muted-foreground hover:text-foreground"
                    >
                      {t('agentic.suggestion.keep_mine')}
                    </Button>
                  </>
                )}
              </div>
            </div>
          </>
        ) : (
          /* The input row — the shared ChatInput (PR-14 strangle): one
             textarea + one button row (attach, mic, Send) in one
             position, per DESIGN.md §4.3's input-pill contract. The
             Eric drawer slot is deliberately OMITTED: this composer's
             Send already routes to the assistant — a drawer here would
             be the assistant inside the assistant. */
          <div className="px-3 pb-3 pt-2.5">
            {/* Transient voice hint (too-short press, mic denied, handoff
                failure) — mirrors ThreadChat's calm slot, auto-cleared. */}
            {voiceHint ? (
              <div
                className="mb-2 flex items-center gap-1.5 text-xs text-muted-foreground animate-in fade-in slide-in-from-bottom-1 duration-150 ease-out motion-reduce:animate-none"
                aria-live="polite"
              >
                <span>{tVoice(`hint_${voiceHint}`)}</span>
              </div>
            ) : null}
            <ChatInput
              ref={chatInputRef}
              value={value}
              onChange={onComposerChange}
              onSubmit={onSubmit}
              submitLabel={submitLabel}
              placeholder={placeholder}
              ariaLabel={placeholder}
              submitting={submitBusy}
              submitOnEnter
              allowEmptySubmit={
                // Empty value is only blocking when the cascade hasn't
                // fully staged a slot yet AND there's no staged photo.
                // In target_with_slot mode the cascade alone carries
                // enough to execute; a staged photo is itself the
                // payload (caption optional) — Send hands it to Eric.
                composer.mode === 'target_with_slot' || staged !== null
              }
              onBackspaceEmpty={
                // Backspace from an empty value peels the last composer
                // step (idle has nothing to peel).
                composer.mode !== 'idle' ? peelBack : undefined
              }
              inputMode={
                composer.mode === 'field' &&
                composer.field.inputMode === 'numeric'
                  ? 'numeric'
                  : undefined
              }
              micSlot={
                <VoiceMicButton
                  disabled={submitBusy || handingOff || !micActive}
                  onClip={(clip) => void onVoiceClip(clip)}
                  // No onClipToText: the dock has no post-handoff composer
                  // for a transcript, so the locked panel offers no "Use as
                  // text" here (capability-driven in VoiceMicButton).
                  onHint={showVoiceHint}
                />
              }
              attachment={{
                onFile: (file) => void onAttachFile(file),
                ariaLabel: t('agentic.input.attach'),
              }}
            />
          </div>
        )}
      </div>

      {/* Time slot chips — appear after /m + target (reschedule) or
          /b + target (book). Tap to STAGE the slot into the cascade
          (composer enters `target_with_slot`). User reviews the
          job description, time pill, then hits Send to execute. */}
      {availableSlotsEnabled ? (
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground">
            {composer.verb === 'book'
              ? t('agentic.book.pick_slot')
              : t('agentic.reschedule.pick_slot')}
          </div>
          <div className="mt-1.5 flex flex-wrap gap-2">
            {(availableSlotsQuery.data?.slots ?? []).map((s) => {
              const stagedSlot =
                composer.mode === 'target_with_slot'
                  ? composer.slot.starts_at_iso
                  : null
              const isStaged = stagedSlot === s.starts_at_iso
              return (
                <Button
                  key={s.starts_at_iso}
                  type="button"
                  onClick={() => {
                    if (
                      composer.mode !== 'target' &&
                      composer.mode !== 'target_with_slot'
                    )
                      return
                    if (
                      composer.verb !== 'book' &&
                      composer.verb !== 'reschedule'
                    )
                      return
                    setComposer({
                      mode: 'target_with_slot',
                      verb: composer.verb,
                      target: composer.target,
                      slot: s,
                    })
                  }}
                  variant="chip"
                  size="pill"
                  className={
                    isStaged
                      ? 'min-h-11 gap-0 border-brand bg-brand/10 px-3.5 text-sm font-medium text-brand [@media(hover:hover)_and_(pointer:fine)]:hover:border-brand [@media(hover:hover)_and_(pointer:fine)]:hover:bg-brand/10'
                      : 'min-h-11 gap-0 px-3.5 text-sm font-normal'
                  }
                >
                  {s.label}
                </Button>
              )
            })}
            {!availableSlotsQuery.isLoading &&
            (availableSlotsQuery.data?.slots ?? []).length === 0 ? (
              <span className="text-xs text-muted-foreground">
                {t('agentic.reschedule.no_slots')}
              </span>
            ) : null}
          </div>
          <div className="mt-1 text-xs text-muted-foreground">
            {haikuSuggestion
              ? t('agentic.compose.note_pending')
              : composer.mode === 'target_with_slot'
                ? t('agentic.compose.slot_staged')
                : composer.verb === 'book'
                  ? t('agentic.book.or_type')
                  : t('agentic.reschedule.or_type')}
          </div>
          {/* Cancel option — only relevant for /m. Visually
              destructive (red text + red-tinted border on hover) so
              it doesn't get confused with the rescheduling slots. */}
          {(composer.mode === 'target' ||
            composer.mode === 'target_with_slot') &&
          composer.verb === 'reschedule' ? (
            <div className="mt-3 border-t border-border pt-3">
              <Button
                type="button"
                onClick={() => {
                  if (
                    composer.mode !== 'target' &&
                    composer.mode !== 'target_with_slot'
                  )
                    return
                  cancelAppointment.mutate({
                    appointment_id: composer.target.id,
                  })
                }}
                disabled={cancelAppointment.isPending}
                variant="chip"
                size="pill"
                className="min-h-11 gap-1.5 border-red-200 bg-card px-3.5 text-sm font-normal text-red-700 [@media(hover:hover)_and_(pointer:fine)]:hover:border-red-300 [@media(hover:hover)_and_(pointer:fine)]:hover:bg-red-50 [@media(hover:hover)_and_(pointer:fine)]:hover:text-red-700"
              >
                <X className="size-4" />
                {t('agentic.reschedule.cancel_appointment')}
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}

      {/* Field chips — appear after /u + target. */}
      {showFieldChips ? (
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground">
            {t('agentic.update.pick_field')}
          </div>
          <div className="mt-1.5 flex flex-wrap gap-2">
            {fieldChipSet.map((f) => (
              <Button
                key={f.key}
                type="button"
                onClick={() => onFieldPicked(f)}
                variant="chip"
                size="pill"
                className="min-h-11 gap-0 px-3.5 text-sm font-normal"
              >
                {t(f.labelKey)}
              </Button>
            ))}
          </div>
        </div>
      ) : null}

      {/* Scoped / unscoped search panel. */}
      {showSearch ? (
        <SearchResults
          results={searchQuery.data?.results ?? []}
          isLoading={searchQuery.isLoading}
          // While a fresh query is in flight the tiles show the PREVIOUS query's
          // results (kept by placeholderData); mark them stale so they dim and
          // ignore taps until the rows match what was typed.
          stale={searchQuery.isPlaceholderData}
          query={debouncedQ}
          onPick={
            composer.mode === 'verb' ||
            // Freeform with a typed verb intent → onPick stages the
            // cascade (handled inside onTargetPicked). Without a verb,
            // leave onPick undefined so the tile navigates to the
            // record page.
            ((composer.mode === 'idle' || composer.mode === 'freeform') &&
              parseFreeformIntent(value) !== null)
              ? onTargetPicked
              : undefined
          }
        />
      ) : null}

      {/* Default customers-without-vehicles list for /v before the user types. */}
      {customersWoVehiclesEnabled ? (
        <div className="mt-2 rounded-xl border border-border bg-card shadow-sm">
          <div className="border-b border-border px-3 py-2 text-xs uppercase tracking-wide text-muted-foreground">
            {t('agentic.compose.customers_without_vehicles')}
          </div>
          <ul className="max-h-80 divide-y divide-border overflow-y-auto">
            {(customersWoVehiclesQuery.data?.results ?? []).map((r) => (
              <li key={`cwv-${r.id}`}>
                <button
                  type="button"
                  onClick={() => onTargetPicked(r)}
                  className="flex min-h-14 w-full items-start gap-3 px-3 py-2 text-left text-sm transition hover:bg-muted"
                >
                  <span
                    className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-brand text-xs font-semibold text-white"
                    aria-hidden
                  >
                    C
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium text-foreground">
                      {r.name}
                    </span>
                    {r.meta ? (
                      <span className="block truncate text-xs text-muted-foreground">
                        {r.meta}
                      </span>
                    ) : null}
                    <span className="block truncate text-xs italic text-foreground/70">
                      {r.reason}
                    </span>
                  </span>
                </button>
              </li>
            ))}
            {!customersWoVehiclesQuery.isLoading &&
            (customersWoVehiclesQuery.data?.results ?? []).length === 0 ? (
              <li className="px-3 py-4 text-sm text-muted-foreground">
                {t('agentic.compose.customers_without_vehicles_empty')}
              </li>
            ) : null}
          </ul>
        </div>
      ) : null}

      {/* Existing-customer suggestions for "Add customer for vehicle":
          when the typed name has near-matches, show them so the user
          taps to link an existing customer instead of creating a
          duplicate. Same dedup contract the brain's create_customer
          tool enforces server-side. */}
      {addCustomerSearchEnabled &&
      composer.mode === 'target' &&
      composer.verb === 'add_customer' &&
      composer.target.kind === 'vehicle' &&
      (addCustomerSearchQuery.data?.results ?? []).length > 0 ? (
        <div className="mt-2 rounded-xl border border-amber-300 bg-amber-50/40 shadow-sm">
          <div className="border-b border-amber-200 px-3 py-2 text-xs uppercase tracking-wide text-amber-900">
            {t('agentic.compose.existing_customer_suggestions', {
              defaultValue: 'Did you mean an existing customer?',
            })}
          </div>
          <ul className="max-h-80 divide-y divide-amber-200 overflow-y-auto">
            {(addCustomerSearchQuery.data?.results ?? []).map((r) => {
              const vehicleTarget =
                composer.mode === 'target' &&
                composer.verb === 'add_customer' &&
                composer.target.kind === 'vehicle'
                  ? composer.target
                  : null
              if (!vehicleTarget) return null
              return (
                <li key={`addcust-existing-${r.id}`}>
                  <button
                    type="button"
                    disabled={addCustomerForVehicle.isPending}
                    onClick={() =>
                      addCustomerForVehicle.mutate({
                        vehicle_id: vehicleTarget.id,
                        customer_id: r.id,
                      })
                    }
                    className="flex min-h-14 w-full items-start gap-3 px-3 py-2 text-left text-sm transition hover:bg-amber-100/50 disabled:opacity-50"
                  >
                    <span
                      className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-amber-500 text-xs font-semibold text-white"
                      aria-hidden
                    >
                      C
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium text-foreground">
                        {r.name}
                      </span>
                      {r.meta ? (
                        <span className="block truncate text-xs text-muted-foreground">
                          {r.meta}
                        </span>
                      ) : null}
                      <span className="block truncate text-xs italic text-amber-900/70">
                        {t('agentic.compose.tap_to_link_existing', {
                          defaultValue: 'Tap to link this existing customer instead',
                        })}
                      </span>
                    </span>
                  </button>
                </li>
              )
            })}
          </ul>
        </div>
      ) : null}

      {/* Default orphaned-vehicles list for /c before the user types. */}
      {orphanedEnabled ? (
        <div className="mt-2 rounded-xl border border-border bg-card shadow-sm">
          <div className="border-b border-border px-3 py-2 text-xs uppercase tracking-wide text-muted-foreground">
            {t('agentic.compose.orphaned_vehicles')}
          </div>
          <ul className="max-h-80 divide-y divide-border overflow-y-auto">
            {(orphanedQuery.data?.results ?? []).map((r) => (
              <li key={`orph-${r.id}`}>
                <button
                  type="button"
                  onClick={() => onTargetPicked(r)}
                  className="flex min-h-14 w-full items-start gap-3 px-3 py-2 text-left text-sm transition hover:bg-muted"
                >
                  <span
                    className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-emerald-500 text-xs font-semibold text-white"
                    aria-hidden
                  >
                    V
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium text-foreground">
                      {r.name}
                    </span>
                    {r.meta ? (
                      <span className="block truncate text-xs text-muted-foreground">
                        {r.meta}
                      </span>
                    ) : null}
                    <span className="block truncate text-xs italic text-foreground/70">
                      {r.reason}
                    </span>
                  </span>
                </button>
              </li>
            ))}
            {!orphanedQuery.isLoading &&
            (orphanedQuery.data?.results ?? []).length === 0 ? (
              <li className="px-3 py-4 text-sm text-muted-foreground">
                {t('agentic.compose.orphaned_vehicles_empty')}
              </li>
            ) : null}
          </ul>
        </div>
      ) : null}

      {/* Default booking-suggestions list for /b before the user types. */}
      {bookingSuggestEnabled ? (
        <div className="mt-2 rounded-xl border border-border bg-card shadow-sm">
          <div className="border-b border-border px-3 py-2 text-xs uppercase tracking-wide text-muted-foreground">
            {t('agentic.compose.booking_suggestions')}
          </div>
          <ul className="max-h-80 divide-y divide-border overflow-y-auto">
            {(bookingSuggestQuery.data?.results ?? []).map((r) => {
              const isHigh = r.urgency === 'high'
              return (
                <li key={`bs-${r.kind}-${r.id}`}>
                  <button
                    type="button"
                    onClick={() => {
                      onTargetPicked(r)
                      setHaikuSuggestion(null)
                      // Two-stage prefill:
                      //   1) Mechanical first-sentence extract goes
                      //      straight into the textarea — instant
                      //      feedback, never silently overwritten.
                      //   2) Haiku summarizer fires in parallel and
                      //      surfaces a "cleaner version" suggestion
                      //      TILE below the textarea. User picks
                      //      whether to apply it (Use this / Keep mine).
                      if (r.note_context) {
                        const firstSentence = r.note_context
                          .split(/[.;\n]/)[0]
                          .trim()
                          .slice(0, 120)
                        if (firstSentence) {
                          requestAnimationFrame(() => setValue(firstSentence))
                          summarizeNote.mutate({
                            note: r.note_context,
                            customer_name: r.name,
                            target_id: r.id,
                          })
                        }
                      }
                    }}
                    className="flex min-h-14 w-full items-start gap-3 px-3 py-2 text-left text-sm transition hover:bg-muted"
                  >
                    <span
                      className={`grid h-8 w-8 shrink-0 place-items-center rounded-full text-xs font-semibold text-white ${
                        r.kind === 'customer' ? 'bg-brand' : 'bg-emerald-500'
                      }`}
                      aria-hidden
                    >
                      {r.kind === 'customer' ? 'C' : 'V'}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-2">
                        <span className="truncate font-medium text-foreground">
                          {r.name}
                        </span>
                        {isHigh ? (
                          <span className="shrink-0 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-red-700">
                            {t('agentic.compose.urgency.high')}
                          </span>
                        ) : (
                          <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700">
                            {t('agentic.compose.urgency.medium')}
                          </span>
                        )}
                      </span>
                      {r.meta ? (
                        <span className="block truncate text-xs text-muted-foreground">
                          {r.meta}
                        </span>
                      ) : null}
                      {r.reason ? (
                        <span className="block truncate text-xs italic text-foreground/70">
                          {r.reason}
                        </span>
                      ) : null}
                    </span>
                  </button>
                </li>
              )
            })}
            {!bookingSuggestQuery.isLoading &&
            (bookingSuggestQuery.data?.results ?? []).length === 0 ? (
              <li className="px-3 py-4 text-sm text-muted-foreground">
                {t('agentic.compose.booking_suggestions_empty')}
              </li>
            ) : null}
          </ul>
        </div>
      ) : null}

      {/* Default suggestions list for /u before the user types.
          Categories: mileage-after-appointment, orphaned, missing-
          critical, missing-optional. Tap with prefill_field set →
          composer jumps straight to the value-input step. */}
      {updateSuggestEnabled ? (
        <div className="mt-2 rounded-xl border border-border bg-card shadow-sm">
          <div className="border-b border-border px-3 py-2 text-xs uppercase tracking-wide text-muted-foreground">
            {t('agentic.compose.update_suggestions')}
          </div>
          <ul className="max-h-96 divide-y divide-border overflow-y-auto">
            {(updateSuggestQuery.data?.results ?? []).map((r) => {
              const categoryStyle =
                r.category === 'mileage_after_appointment'
                  ? 'bg-sky-100 text-sky-700'
                  : r.category === 'orphaned_vehicle'
                    ? 'bg-purple-100 text-purple-700'
                    : r.urgency === 'high'
                      ? 'bg-red-100 text-red-700'
                      : 'bg-amber-100 text-amber-700'
              return (
                <li key={`us-${r.kind}-${r.id}-${r.category}`}>
                  <button
                    type="button"
                    onClick={() => {
                      onTargetPicked(r)
                      // If the suggestion comes with a known field hint,
                      // auto-advance to the field state so the user
                      // lands directly on the value input.
                      if (r.prefill_field) {
                        const fieldSet =
                          r.kind === 'customer' ? CUSTOMER_FIELDS : VEHICLE_FIELDS
                        const field = fieldSet.find(
                          (f) => f.key === r.prefill_field,
                        )
                        if (field) {
                          requestAnimationFrame(() => {
                            setComposer({
                              mode: 'field',
                              verb: 'update',
                              target: r,
                              field,
                            })
                          })
                        }
                      }
                    }}
                    className="flex min-h-14 w-full items-start gap-3 px-3 py-2 text-left text-sm transition hover:bg-muted"
                  >
                    <span
                      className={`grid h-8 w-8 shrink-0 place-items-center rounded-full text-xs font-semibold text-white ${
                        r.kind === 'customer' ? 'bg-brand' : 'bg-emerald-500'
                      }`}
                      aria-hidden
                    >
                      {r.kind === 'customer' ? 'C' : 'V'}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-2">
                        <span className="truncate font-medium text-foreground">
                          {r.name}
                        </span>
                        <span
                          className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${categoryStyle}`}
                        >
                          {t(`agentic.compose.update_category.${r.category}`)}
                        </span>
                      </span>
                      {r.meta ? (
                        <span className="block truncate text-xs text-muted-foreground">
                          {r.meta}
                        </span>
                      ) : null}
                      {r.reason ? (
                        <span className="block truncate text-xs italic text-foreground/70">
                          {r.reason}
                        </span>
                      ) : null}
                    </span>
                  </button>
                </li>
              )
            })}
            {!updateSuggestQuery.isLoading &&
            (updateSuggestQuery.data?.results ?? []).length === 0 ? (
              <li className="px-3 py-4 text-sm text-muted-foreground">
                {t('agentic.compose.update_suggestions_empty')}
              </li>
            ) : null}
          </ul>
        </div>
      ) : null}

      {/* Default upcoming-appointments list for /m before the user types. */}
      {upcomingEnabled ? (
        <div className="mt-2 rounded-xl border border-border bg-card shadow-sm">
          <div className="border-b border-border px-3 py-2 text-xs uppercase tracking-wide text-muted-foreground">
            {t('agentic.compose.upcoming_appointments')}
          </div>
          <ul className="max-h-72 divide-y divide-border overflow-y-auto">
            {(upcomingQuery.data?.results ?? []).map((r) => (
              <li key={`upcoming-${r.id}`}>
                <button
                  type="button"
                  onClick={() => onTargetPicked(r)}
                  className="flex min-h-14 w-full items-center gap-3 px-3 py-2 text-left text-sm transition hover:bg-muted"
                >
                  <span
                    className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-amber-500 text-xs font-semibold text-white"
                    aria-hidden
                  >
                    A
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium text-foreground">
                      {r.name}
                    </span>
                    {r.meta ? (
                      <span className="block truncate text-xs text-muted-foreground">
                        {r.meta}
                      </span>
                    ) : null}
                  </span>
                </button>
              </li>
            ))}
            {!upcomingQuery.isLoading &&
            (upcomingQuery.data?.results ?? []).length === 0 ? (
              <li className="px-3 py-4 text-sm text-muted-foreground">
                {t('agentic.search.empty', { query: '' })}
              </li>
            ) : null}
          </ul>
        </div>
      ) : null}

      {/* Cascade permission-denied surface (server-side 403 from any
          /api/dashboard/* mutation). Mirrors the brain's behavior over
          SMS when a tool is gated. */}
      {cascadeError ? (
        <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <div className="flex flex-wrap items-start gap-2">
            <div className="flex-1">
              <div className="text-xs font-medium uppercase tracking-wide">
                {cascadeError.kind === 'forbidden' ? (
                  <>
                    {t('agentic.forbidden.label')}{' '}
                    <span className="text-amber-900/60">
                      ({cascadeError.capability})
                    </span>
                  </>
                ) : (
                  t('agentic.cascade_failed.label')
                )}
              </div>
              <p className="mt-1">
                {cascadeError.kind === 'forbidden'
                  ? t('agentic.forbidden.body')
                  : t('agentic.cascade_failed.body')}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setCascadeError(null)}
              className="text-xs text-amber-900/60 hover:text-amber-900"
            >
              {t('agentic.forbidden.dismiss')}
            </button>
          </div>
        </div>
      ) : null}

      {/* Executed acks: consumed by the dock-seam snapshot above and
          rendered by the host as §4.1 receipts (ReceiptsHost UNDER the
          composer — DESIGN.md §4.3 spatial contract). The legacy inline
          green banner that rendered here was strangled in PR-14. */}
      {/* Screen-reader live region. The visual cards below mount/unmount per
          phase, which screen readers announce unreliably; this PERSISTENT
          region narrates the in-flight phase instead. The completed/undone
          side of the flow is announced by the host's ReceiptsHost (its stack
          is an aria-live region) — the legacy banner + undo announcements
          left with the banner (PR-14). */}
      <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {dispatchPending
          ? t('agentic.stream.working', { defaultValue: 'Eric is working…' })
          : ''}
      </div>

      {/* Live streaming progress (Deploy #7) — tool-progress chips + the reply
          text as it generates. Replaced by the result/reply card the moment the
          final `done` envelope lands. Hidden during a slot-tap (the reply card
          shows its own "Booking…" pill there). */}
      {dispatchPending && !slotTapPending && streamProgress ? (
        <div className="rounded-xl border border-border bg-card px-4 py-3 text-sm text-foreground">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
            <Loader2 className="size-3.5 animate-spin" />
            {t('agentic.stream.working', { defaultValue: 'Eric is working…' })}
          </div>
          {streamProgress.tools.length > 0 ? (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {streamProgress.tools.map((tool, i) => (
                <span
                  key={`${tool}-${i}`}
                  className="inline-flex items-center rounded-full border border-border bg-muted px-2.5 py-0.5 text-xs text-muted-foreground"
                >
                  {toolProgressLabel(tool, t)}
                </span>
              ))}
            </div>
          ) : null}
          {streamProgress.text ? (
            <p className="mt-2 whitespace-pre-wrap break-words">{streamProgress.text}</p>
          ) : null}
        </div>
      ) : null}

      {/* (Staged-attachment preview + chips render inside the composer card
          above — see the `{staged ? …}` block.) */}

      {/* Free-form ack. When the brain declined (refuse_*) or errored
          (error_*), tone the card amber so the user can tell apart a
          thoughtful answer from a refusal / failure at a glance. */}
      {dispatchResult?.mode === 'reply' ? (() => {
        const gd = dispatchResult.guardrail_decision ?? ''
        const isRefusal = gd.startsWith('refuse_')
        const isError = gd.startsWith('error_')
        const toneClass = isRefusal || isError
          ? 'rounded-xl border border-amber-300 bg-amber-50/60 px-4 py-3 text-sm text-amber-950'
          : 'rounded-xl border border-border bg-card px-4 py-3 text-sm text-foreground'
        const labelKey = isRefusal
          ? 'agentic.reply.refusal_label'
          : isError
            ? 'agentic.reply.error_label'
            : 'agentic.reply.label'
        // A `book` reply asking which customer → surface an inline customer
        // combobox (takes precedence over the slot strip: you need the
        // customer before the time).
        const showReplyCustomerPicker =
          !isRefusal && !isError && replyCustomerPickerEnabled
        // When the staged cascade was a book/reschedule and the brain
        // is asking for a time, surface the same slot strip the
        // cascade UI shows so the user can tap-to-pick instead of
        // typing the time and re-sending.
        const showReplySlots =
          !isRefusal &&
          !isError &&
          !showReplyCustomerPicker &&
          (lastDispatchContext.verb === 'book' ||
            lastDispatchContext.verb === 'reschedule')
        const replySlots = replyCardSlotsQuery.data?.slots ?? []
        // The slot picker is CONSUMED once a booking for this context
        // generation settled (fast lane or brain) — rendered inert below
        // so a stale strip can't book a second appointment.
        const slotPickerConsumed = isSlotPickerConsumed(
          lastDispatchContext.id,
          consumedContextId,
        )
        // The fast-lane slot tap books via useMutation, not the stream, so
        // the in-flight pill + chip disabling key off the mutations too.
        const slotTapInFlight =
          slotTapPending &&
          (dispatch.isPending || book.isPending || reschedule.isPending)
        const replyCustomerResults = (
          replyCustomerSearchQuery.data?.results ?? []
        ).filter((r) => r.kind === 'customer')
        const onPickReplyCustomer = (cust: SearchResult) => {
          if (cust.kind !== 'customer') return
          // Resolve the ambiguous/missing customer for the book the user
          // asked about, then let the brain continue — it surfaces the slot
          // strip next if it still needs a time (the new "what time?" reply
          // won't match replyAsksForCustomer, so this picker won't re-show).
          // Lean on session memory for the rest, mirroring the slot-tap path.
          // Pin the pick into the dispatch context too (same generation —
          // it's a continuation, not a new flow) so a follow-up slot tap
          // sees a RESOLVED customer and takes the fast lane instead of
          // another brain round-trip.
          setLastDispatchContext((prev) => ({ ...prev, customer: cust }))
          const prompt = `[CASCADE: verb=book customer_id=${cust.id} customer_name="${cust.name}"]\nContinuing the previous turn — use this customer for the booking I just asked about. Resolve the rest from your session memory of my last message; ask for the time if you still need it.`
          setSlotTapPending(true)
          setReplyCustomerQ('')
          setReplyCustomerDebounced('')
          dispatch.mutate(prompt)
        }
        const onPickReplySlot = (slot: { starts_at_iso: string; label: string }) => {
          if (!lastDispatchContext.verb) return
          // Consumed / in-flight strips are inert — the disabled chips are
          // the visual layer; this guard is the behavioral backstop.
          if (slotPickerConsumed || slotTapInFlight) return
          // A slot tap with the booking target already resolved is fully-
          // resolved structured work → the deterministic cascade fast lane
          // (architecture §5), NOT a brain round-trip (the brain refuses the
          // [CASCADE:] directive as a system-style override — live EMSERVICE
          // failure 2026-06-11). Routes through the same book/reschedule
          // mutations the composer cascade uses, so the success banner +
          // Undo + invalidations are identical. An unresolved context falls
          // through to the brain-dispatch path below, unchanged.
          const route = selectSlotTapRoute(lastDispatchContext)
          if (route.lane === 'book') {
            const ctxId = lastDispatchContext.id
            setSlotTapPending(true)
            book.mutate(
              {
                customer_id: route.customerId,
                starts_at_iso: slot.starts_at_iso,
                notes: lastDispatchContext.note,
              },
              {
                onSuccess: () =>
                  setConsumedContextId((prev) => consumeContextId(prev, ctxId)),
                onSettled: () => setSlotTapPending(false),
              },
            )
            return
          }
          if (route.lane === 'reschedule') {
            const ctxId = lastDispatchContext.id
            setSlotTapPending(true)
            reschedule.mutate(
              {
                appointment_id: route.appointmentId,
                new_starts_at_iso: slot.starts_at_iso,
              },
              {
                onSuccess: () =>
                  setConsumedContextId((prev) => consumeContextId(prev, ctxId)),
                onSettled: () => setSlotTapPending(false),
              },
            )
            return
          }
          const target =
            lastDispatchContext.customer ??
            lastDispatchContext.vehicle ??
            lastDispatchContext.appointment
          // Two brain paths (unresolved fallback):
          //   - Cascade was staged → build a full [CASCADE: ...] prompt
          //     with the resolved ids + tapped slot. Brain executes
          //     immediately, no rediscovery.
          //   - Pure freeform → no resolved target on hand; lean on the
          //     brain's session memory of the prior turn (which named
          //     the customer/vehicle in the user's original prompt) and
          //     send a "use this slot" follow-up. Brain stitches the
          //     prior context to the new time and executes.
          const prompt = target
            ? `${composeCascadePrefix(
                lastDispatchContext.verb,
                target,
                slot,
              )} (Slot picked from reply card.)`
            : `[CASCADE: verb=${lastDispatchContext.verb} when_iso=${slot.starts_at_iso} when_label="${slot.label}"]\nContinuing the previous turn — use this slot for the ${lastDispatchContext.verb === 'reschedule' ? 'reschedule' : 'booking'} I just asked about. Customer + vehicle context come from your session memory of my last message; execute the matching tool immediately.`
          // Keep the reply card mounted across the round-trip so the
          // dashboard doesn't flash empty for 3-6s while the brain
          // works. The inline pending pill (rendered below) replaces
          // the slot strip while slotTapInFlight.
          setSlotTapPending(true)
          dispatch.mutate(prompt)
        }
        return (
          <div className={toneClass}>
            <div className="text-xs uppercase tracking-wide text-muted-foreground">
              {t(labelKey, { defaultValue: t('agentic.reply.label') })}
            </div>
            <p className="mt-1">{dispatchResult.reply}</p>
            {showReplyCustomerPicker ? (
              dispatch.isPending && slotTapPending ? (
                // Pick is in flight — pending pill in place of the picker so
                // the card stays informative without re-prompting.
                <div className="mt-3 inline-flex items-center gap-2 rounded-full border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-900">
                  <Loader2 className="size-3.5 animate-spin" />
                  {t('agentic.reply.customer_pending', {
                    defaultValue: 'Setting customer…',
                  })}
                </div>
              ) : (
                <div className="mt-3 rounded-lg border border-amber-200 bg-white/60 p-2.5">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-amber-900">
                    {t('agentic.reply.pick_customer_label', {
                      defaultValue: 'Pick a customer',
                    })}
                  </div>
                  <Input
                    type="text"
                    value={replyCustomerQ}
                    onChange={(e) => setReplyCustomerQ(e.target.value)}
                    placeholder={t('agentic.reply.pick_customer_placeholder', {
                      defaultValue: 'Search customers…',
                    })}
                    className="mt-1.5 h-auto min-h-11 rounded-md border-amber-300 bg-white px-2.5 text-base text-foreground focus-visible:border-amber-500 focus-visible:ring-1 focus-visible:ring-amber-400 md:text-sm"
                  />
                  {replyCustomerDebounced.length < 2 ? null : replyCustomerSearchQuery.isLoading ? (
                    <div className="mt-1.5 text-xs text-muted-foreground">
                      {t('agentic.reply.pick_customer_loading', {
                        defaultValue: 'Searching…',
                      })}
                    </div>
                  ) : replyCustomerResults.length === 0 ? (
                    <div className="mt-1.5 text-xs text-muted-foreground">
                      {t('agentic.reply.pick_customer_empty', {
                        defaultValue: 'No matching customers.',
                      })}
                    </div>
                  ) : (
                    <div className="mt-1.5 flex flex-col gap-1">
                      {replyCustomerResults.map((r) => (
                        <button
                          key={r.id}
                          type="button"
                          disabled={
                            dispatch.isPending ||
                            book.isPending ||
                            reschedule.isPending
                          }
                          onClick={() => onPickReplyCustomer(r)}
                          className="flex min-h-11 flex-col justify-center rounded-md border border-amber-200 bg-white px-3 py-1 text-left transition hover:border-amber-400 hover:bg-amber-50 active:scale-[0.99] disabled:opacity-50"
                        >
                          <span className="text-sm font-medium text-amber-900">
                            {r.name}
                          </span>
                          {r.meta ? (
                            <span className="text-xs text-amber-700">{r.meta}</span>
                          ) : null}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )
            ) : null}
            {showReplySlots ? (
              slotTapInFlight ? (
                // Slot tap is in flight (brain dispatch OR fast-lane
                // mutation). Show a pending pill in place of the slot strip
                // so the reply card stays informative without re-prompting.
                <div className="mt-3 inline-flex items-center gap-2 rounded-full border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-900">
                  <Loader2 className="size-3.5 animate-spin" />
                  {t('agentic.reply.booking_pending', {
                    defaultValue: 'Booking…',
                  })}
                </div>
              ) : (
                // Consumed strip (booking already settled): dimmed +
                // non-interactive, mirroring the SearchResults stale
                // treatment, so a stale strip can't book twice.
                <div
                  className={`mt-3 rounded-lg border border-amber-200 bg-white/60 p-2.5 ${
                    slotPickerConsumed ? 'pointer-events-none opacity-60' : ''
                  }`}
                >
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-amber-900">
                    {t('agentic.reply.pick_slot_label', {
                      defaultValue: 'Tap a slot to book',
                    })}
                  </div>
                  {replyCardSlotsQuery.isLoading ? (
                    <div className="mt-1.5 text-xs text-muted-foreground">
                      {t('agentic.preview.pick_slot_loading', {
                        defaultValue: 'Finding open slots…',
                      })}
                    </div>
                  ) : replySlots.length === 0 ? (
                    <div className="mt-1.5 text-xs text-muted-foreground">
                      {t('agentic.preview.pick_slot_empty', {
                        defaultValue: 'No open slots in the next 2 weeks.',
                      })}
                    </div>
                  ) : (
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {replySlots.map((s) => (
                        <Button
                          key={s.starts_at_iso}
                          type="button"
                          disabled={
                            dispatch.isPending ||
                            book.isPending ||
                            reschedule.isPending ||
                            slotPickerConsumed
                          }
                          onClick={() => onPickReplySlot(s)}
                          variant="warning"
                          size="pill-sm"
                          className="min-h-9 gap-0 bg-white px-3 text-xs font-medium text-amber-900"
                        >
                          {s.label}
                        </Button>
                      ))}
                    </div>
                  )}
                </div>
              )
            ) : null}
            <button
              type="button"
              onClick={() => setDispatchResult(null)}
              className="mt-2 text-xs text-muted-foreground hover:text-foreground"
            >
              {t('agentic.success.dismiss')}
            </button>
          </div>
        )
      })() : null}

      {/* Blank-card safety net (Fix 4). A turn ended with no usable reply — a
          transport fault, a truncated stream, or an empty finalize the server
          fallback couldn't recover. Show a retry affordance, never a blank box.
          Gated on !dispatchResult so it can never stack behind a result card if a
          subsequent inline action lands a result before the failure was cleared. */}
      {dispatchFailed && !dispatchResult ? (
        <div className="rounded-xl border border-amber-300 bg-amber-50/60 px-4 py-3 text-sm text-amber-950">
          <div className="text-xs uppercase tracking-wide text-amber-700">
            {t('agentic.reply.incomplete_label', { defaultValue: "Eric didn't finish" })}
          </div>
          <p className="mt-1">
            {incompleteReply ??
              t('agentic.reply.incomplete_body', {
                defaultValue: "That response didn't come through. Tap retry to run it again.",
              })}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button
              type="button"
              onClick={retryLastDispatch}
              disabled={dispatch.isPending || !lastDispatch}
              variant="default"
              size="pill"
              className="gap-1.5 border-amber-700 bg-amber-700 px-4 text-sm font-semibold text-white hover:bg-amber-800"
            >
              <RotateCw className="size-3.5" aria-hidden />
              {t('agentic.reply.retry', { defaultValue: 'Retry' })}
            </Button>
            <Button
              type="button"
              onClick={dismissDispatchFailure}
              variant="ghost"
              size="pill"
              className="px-3 text-xs font-medium text-amber-900 [@media(hover:hover)_and_(pointer:fine)]:hover:bg-transparent [@media(hover:hover)_and_(pointer:fine)]:hover:text-amber-950"
            >
              {t('agentic.success.dismiss')}
            </Button>
          </div>
        </div>
      ) : null}

      {/* Idle verb chips (§4.3): one horizontally-scrollable row on
          phone (wrap returns at lg), no eyebrow label — the dock IS the
          "start with" affordance. (The undocked eyebrow + wrapped-block
          variant left with the optional-onExecuted mode in PR-14.) */}
      {showVerbChips ? (
        <div>
          <div className="mt-1.5 flex items-center gap-2 overflow-x-auto pb-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden lg:flex-wrap lg:overflow-x-visible">
            {VERBS.map((v) => (
              <Button
                key={v}
                type="button"
                onClick={() => startVerb(v)}
                variant="chip"
                size="pill"
                className="min-h-11 shrink-0 gap-1.5 px-3.5 text-sm font-normal"
              >
                <span>{t(VERB_LABEL_KEY[v])}</span>
                <kbd
                  aria-hidden="true"
                  className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-mono text-muted-foreground"
                >
                  {VERB_SLASH[v]}
                </kbd>
              </Button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  )
}

function ComposerPill({
  label,
  onRemove,
}: {
  label: string
  onRemove: () => void
}) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-brand/10 px-2.5 py-1 text-xs font-medium text-brand">
      {label}
      <button
        type="button"
        onClick={onRemove}
        aria-label="Remove step"
        className="grid h-4 w-4 place-items-center rounded-full text-brand/70 transition hover:bg-brand/20 hover:text-brand"
      >
        <X className="size-3" />
      </button>
    </span>
  )
}

function Caret() {
  return <span className="text-muted-foreground">›</span>
}

// ─── Compose helpers ─────────────────────────────────────────────────

// Friendly progress labels for the brain's tools, shown as chips in the streaming
// progress card (Deploy #7). Module-scoped (built once, not per call). A tool not
// listed here falls back to a generic "Working…" so a newly-added brain tool never
// surfaces a raw snake_case name. i18n via defaultValue (same inline pattern as
// the reply-card slot strings).
const TOOL_PROGRESS_LABELS: Record<string, string> = {
  decode_vin: 'Decoding VIN…',
  lookup_customer: 'Looking up customer…',
  lookup_vehicle: 'Looking up vehicle…',
  create_customer: 'Adding customer…',
  update_customer: 'Updating customer…',
  create_vehicle: 'Adding vehicle…',
  update_vehicle: 'Updating vehicle…',
  create_appointment: 'Booking…',
  list_appointments: 'Checking the calendar…',
  update_appointment: 'Updating appointment…',
  update_vehicle_notes: 'Saving notes…',
  flag_carryover_note: 'Flagging a note…',
  acknowledge_carryover_note: 'Updating a note…',
  list_tags: 'Checking tags…',
  tag_customer: 'Updating customer tags…',
  tag_vehicle: 'Updating vehicle tags…',
  shop_stats: 'Counting…',
  undo_last_action: 'Undoing…',
  draft_estimate: 'Drafting estimate…',
  summarize_customer_history: 'Reviewing history…',
  summarize_vehicle_history: 'Reviewing history…',
}

function toolProgressLabel(
  toolName: string,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  return t(`agentic.stream.tool.${toolName}`, {
    defaultValue: TOOL_PROGRESS_LABELS[toolName] ?? 'Working…',
  })
}

function composeAddPrefix(verb: Verb): string {
  if (verb === 'add_customer') return 'Add customer:'
  if (verb === 'add_vehicle') return 'Add vehicle:'
  if (verb === 'update') return 'Update:'
  if (verb === 'book') return 'Book:'
  if (verb === 'reschedule') return 'Reschedule:'
  return ''
}

// Build a directive [CASCADE: ...] context block the brain can parse +
// act on without rediscovering ids the dashboard already resolved.
// When `slot` is present we instruct the brain to execute the action
// immediately (the user explicitly picked it from the slot strip);
// when slot is absent, the brain treats this as a partial spec and
// asks for any missing piece. Free-form text is appended verbatim so
// any human notes flow into the visit description.
function composeCascadePrefix(
  verb: Verb,
  target: SearchResult,
  slot: { starts_at_iso: string; label: string } | null,
): string {
  // Compact key=value parts so Sonnet can read both as structured
  // hints and as natural language.
  const fields: string[] = [`verb=${verb}`]
  if (target.kind === 'customer') {
    fields.push(`customer_id=${target.id}`)
    fields.push(`customer_name="${target.name}"`)
  } else if (target.kind === 'vehicle') {
    fields.push(`vehicle_id=${target.id}`)
    fields.push(`vehicle_label="${target.name}"`)
  } else if (target.kind === 'appointment') {
    fields.push(`appointment_id=${target.id}`)
    fields.push(`appointment_label="${target.name}"`)
  }
  if (slot) {
    fields.push(`when_iso=${slot.starts_at_iso}`)
    fields.push(`when_label="${slot.label}"`)
  }
  // Keep the user-side directive short (brain's stage-1 rule filter
  // caps inbound at 500 chars). The strict "book first, no clarifying
  // questions" rule lives in the system prompt's Cascade directive
  // clause (worker/lib/eric-brain.ts) where length isn't a constraint.
  const directive = slot
    ? `Execute the matching create_/update_ tool NOW with the ids + time above. Book first, ask after.`
    : `Use the ids + verb above as fixed context. Resolve missing details from the user's note; ask one short clarifying question only if the action still can't run.`
  return `[CASCADE: ${fields.join(' ')}]\n${directive}\n\nUser note:`
}

// Lightweight intent sniffer for pure freeform prompts (no cascade
// staged). Lets the reply card render an inline slot strip when Eric
// asks "what time?" on a clear book/reschedule prompt — same UX a
// staged cascade gets, without making the user re-type through the
// chip flow. Conservative on purpose: false positives just render an
// extra slot strip; false negatives fall back to a text-only reply.
// Does Eric's reply read like a request to disambiguate / name the customer?
// Gates the reply-card customer picker so it fires ONLY on a "which customer?"
// reply — a "what time?" reply (no cue here) keeps the slot strip untouched.
// Conservative: a miss falls back to the text-only reply (the user types the
// name, the prior behavior), so a false negative never regresses anything.
function replyAsksForCustomer(reply: string): boolean {
  const r = reply.toLowerCase()
  // Every alternative is anchored to a customer/client/person noun. Bare
  // "did you mean", "which one", "no match", "matching" are deliberately
  // EXCLUDED — they fire on time/slot disambiguation ("Did you mean 3pm or
  // 4pm?", "No matching slots — which one works?") and would wrongly suppress
  // the slot strip on a freeform book.
  return /\b(which (?:customer|client|person|one of (?:the )?(?:customers|clients))|who(?:'s| is| are)? (?:this|it|they)\b|who would you like|couldn'?t find (?:a |the )?(?:customer|client|anyone)|can'?t find (?:a |the )?(?:customer|client)|no (?:customer|client|matching customer|matching client)|multiple (?:customers|clients|people)|more than one (?:customer|client))\b/.test(
    r,
  )
}

function parseFreeformIntent(prompt: string): Verb | null {
  const p = prompt.trim().toLowerCase()
  if (p.length === 0) return null
  // Reschedule beats book — "reschedule" + "book" can co-occur ("I
  // need to reschedule the booking"), and reschedule is the more
  // specific verb. Word boundaries on both sides so we don't match
  // "scheduled" or "rebook" partials.
  if (/\b(reschedul|move\s+(it|the\s+appt)|push\s+back|shift|change\s+(the\s+)?(time|appointment|booking))/i.test(p))
    return 'reschedule'
  if (/\b(book|schedule|appointment|appt|fit\s+(him|her|them|in)|set\s+up\s+(an?\s+)?(visit|appointment))\b/i.test(p))
    return 'book'
  return null
}

// Extract just the customer (or appointment) name from a freeform
// prompt so the name-only fallback search can find them when the
// full-phrase ILIKE returns 0. Strips the leading verb keyword + the
// trailing filler that follows the name ("for wednesday", "at 10am",
// "tomorrow morning", etc.). Returns null when nothing parseable is
// left or when the result is too short to be a useful query.
//
// Examples:
//   ("book olivia for wednesday", 'book')        → "olivia"
//   ("schedule maria tomorrow morning", 'book')  → "maria"
//   ("reschedule the civic appt", 'reschedule')  → "the civic"  (3+ chars, fine)
//   ("book", 'book')                             → null         (nothing after verb)
function extractNameQuery(prompt: string, verb: Verb): string | null {
  if (verb !== 'book' && verb !== 'reschedule') return null
  let s = prompt.trim()
  // Strip the matching verb prefix (with optional "an"/"a"/"the" filler).
  s = s.replace(
    /^\s*(book|schedul(?:e|ed|ing)?|reschedul(?:e|ed|ing)?|appointment|appt|set\s+up\s+(?:an?\s+)?(?:visit|appointment))\s+(an?\s+|the\s+)?/i,
    '',
  )
  // Cut at the first time/date filler so the name doesn't drag in
  // "for wednesday at 10am" etc.
  const cutAt = s.search(
    /\b(for|on|at|in|next|this|tomorrow|today|tonight|morning|afternoon|evening|noon|mon(?:day)?|tue(?:sday)?|wed(?:nesday)?|thu(?:rsday)?|fri(?:day)?|sat(?:urday)?|sun(?:day)?|\d)/i,
  )
  if (cutAt >= 0) s = s.slice(0, cutAt)
  s = s.trim().replace(/[,;:.!?]+$/, '').trim()
  if (s.length < 3) return null
  return s
}

// Renders the textarea-position diff between the user's current
// value and Haiku's proposed cleaner version. Kept-text reads normally;
// removed-text shows as red strikethrough; inserted-text shows in brand
// color with a subtle underline. Lives in the same space the textarea
// would occupy so the visual swap doesn't shift layout.
function DiffPreview({
  original,
  suggestion,
}: {
  original: string
  suggestion: string
}) {
  const segments = wordDiff(original, suggestion)
  return (
    <div
      aria-label="Eric's proposed edit"
      className="block min-h-[3.5rem] w-full whitespace-pre-wrap break-words border-0 bg-transparent px-4 py-3 text-base text-foreground"
    >
      {segments.map((seg, i) => {
        if (seg.type === 'kept') return <span key={i}>{seg.text}</span>
        if (seg.type === 'removed')
          return (
            <span key={i} className="text-red-600 line-through decoration-red-500">
              {seg.text}
            </span>
          )
        return (
          <span
            key={i}
            className="rounded bg-brand/10 text-brand underline decoration-brand/40 underline-offset-2"
          >
            {seg.text}
          </span>
        )
      })}
    </div>
  )
}

// Word-level diff via standard LCS. Whitespace is preserved as its
// own tokens so the rendered output reads naturally instead of
// collapsing spaces between marked segments.
type DiffSegment = { type: 'kept' | 'removed' | 'inserted'; text: string }

function wordDiff(a: string, b: string): DiffSegment[] {
  const tokens = (s: string) => (s.length === 0 ? [] : s.split(/(\s+)/))
  const aw = tokens(a)
  const bw = tokens(b)
  const n = aw.length
  const m = bw.length
  // LCS table (n+1 by m+1).
  const lcs: number[][] = Array.from({ length: n + 1 }, () =>
    new Array(m + 1).fill(0),
  )
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      if (aw[i] === bw[j]) lcs[i][j] = lcs[i + 1][j + 1] + 1
      else lcs[i][j] = Math.max(lcs[i + 1][j], lcs[i][j + 1])
    }
  }
  // Walk the table to produce the diff.
  const out: DiffSegment[] = []
  let i = 0
  let j = 0
  while (i < n && j < m) {
    if (aw[i] === bw[j]) {
      out.push({ type: 'kept', text: aw[i] })
      i++
      j++
    } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
      out.push({ type: 'removed', text: aw[i] })
      i++
    } else {
      out.push({ type: 'inserted', text: bw[j] })
      j++
    }
  }
  while (i < n) {
    out.push({ type: 'removed', text: aw[i++] })
  }
  while (j < m) {
    out.push({ type: 'inserted', text: bw[j++] })
  }
  return out
}

// Vehicle + customer freeform parsers live in `src/lib/parse.ts` so the
// dashboard cascade and any future worker-side validation share the
// same regex constants. Imported at the top of this file.
